/**
 * Pull plan grid workspace.
 *
 *   Left palette         Grid (rows = zones, columns = workdays)
 *   ─────────────        ────────────────────────────────────
 *   🟦 FP Rough          ZONE (A/B/C)    | Mon Tue Wed Thu ...
 *   🟧 Firestopping      ▾STAIR ▾BlkA ▾STR1│ [FP Rough] [Firest]
 *   🟩 Inspection        ▾STAIR ▾BlkA ▾STR2│ [FP Rough]
 *   ━━━━━━━━━━━━━        [+ Add row]
 *   [+ New activity]
 *
 * Activities (defined in left palette) = Takt_Work codes — colored chips
 * dragged from the palette and dropped onto grid cells to create instances.
 * Each grid row is ONE unique zone (Takt_A, Takt_B, Takt_C) — rows that share a
 * zone are consolidated into a single labeled block (`consolidateZones`), and
 * overlapping bars within a zone stack into lanes automatically. Drop a chip on
 * a zone's cells (or the phantom strip below it) to add a bar to that zone.
 *
 * Selection: drag a marquee over bars (auto-scrolls at the edges), shift-click
 * to toggle one, or the ☑ on a zone block to select that whole section. Bulk
 * delete / duplicate-to-new-section act on the selection. Undo (⌘/Ctrl+Z or the
 * Undo button) reverts the last grid edit.
 */
import React from "react";
import {
  fetchDepEditorGraph,
  fetchTaktSectionWbsNodeIds,
  searchActivities,
} from "../../../api/clientV2";
import { type DepCandidate, type ExternalCandidate } from "../depCore/DepChipTypeahead/DepChipTypeahead";
import type {
  DepGraph,
  DepGraphPredType,
  DraftPayload,
  InScopeOrphan,
  PendingTaktification,
  PendingWorkCodeAdd,
  ExistingWorkCode,
  PullPlanSectionContext,
  PullTargetChoice,
  ScopeMember,
  TrainDepType,
  TrainState,
} from "../../../models";

import type { AnchorPublishStats, SeveredTieFlag } from "./state";
import { emptyDraftPayload, emptyTrain, fsPlacementOrder, removeTrainDep, replaceTrainDep, simulateTrain } from "../../../models";
import { notify } from "../../../components/Notice/noticeStore";
import {
  isTrainCarId,
  placementTieEdits,
  TRAIN_ID_BASE,
  TRAIN_LEAF_KEY,
  type SelectionCar,
} from "./train/trainReconcile";
import { fromHereSelection, previewRefs } from "./train/trainSelect";
import { formatLagSuffix } from "../../../utils/p6";
import { lvlWord, actWord } from "../../../utils/plural";
import AddWorkCodePopover from "../AddWorkCodePopover/AddWorkCodePopover";
import CodeEditModal from "../CodeEditModal/CodeEditModal";
import { addEdge, removeEdge, wouldCreateCycle } from "../depCore/edgeIntents";
import { activityLabel, nodeStatus } from "../depCore/graphRows";
import { buildCandidates } from "../depCore/candidates";
import { computeLeafRows, type Bar, type LeafRow } from "../WbsBoardView/boardLayout";
import SequenceView, { collectAnchorKeys } from "../SequenceView/SequenceView";
import { type ApplyEditArgs } from "../depCore/DepTable/EditActivityRow/EditActivityRow";
import DeleteImpactModal, {
  type DeleteImpactTie,
} from "../DeleteImpactModal/DeleteImpactModal";
import HistoryPanel from "./HistoryPanel/HistoryPanel";
import PlanTitleMenu from "./PlanTitleMenu/PlanTitleMenu";
import CreateTrainMenu from "./CreateTrainMenu/CreateTrainMenu";
import AnchorsBoard from "../AnchorsBoard/AnchorsBoard";
import WbsBoardView, { type AnchorDir, type BarSpan } from "../WbsBoardView/WbsBoardView";
import { boardDatesDirty } from "./boardReflect";
import { buildBoardFsLinks } from "./boardFsLinks";
import { computeInlineAddPlacement, type LeafInfo } from "./inlineAddPlacement";
import UnifiedBoard, { PaneModeToggle } from "../UnifiedBoard/UnifiedBoard";
import TrainBand from "../TrainBand/TrainBand";
import {
  type LaneMember,
  type LedgerSnapshot,
} from "../ScopeLedger/ScopeLedger";
import {
  buildInScopeOrphans,
  memberSpan,
  pruneScopeDecisions,
} from "../ScopeLedger/laneState";
import {
  buildEffectiveDraft,
  deleteActivities,
  removeFromWorkCode,
  stageDurationEdit,
  stageRenameEdit,
  stageRezone,
  stageTaktifications,
} from "../draft/draftOps";
import type { ActivityInstance, ZoneRow } from "../draft/types";
import ActivityPalette from "./ActivityPalette/ActivityPalette";
import BulkDropFlow from "./BulkDropFlow/BulkDropFlow";
import DropConflictFlow, { type DropConflict } from "./DropConflictFlow/DropConflictFlow";
import DayHeader from "./DayHeader/DayHeader";
import type { GridSnapshot, ProtoNewAct } from "./state";
import {
  DAY_PX,
  ensureZoneRows,
  foldedZoneKey,
  hashColor,
  indexOnAxis,
  LANE_HEIGHT,
  LANE_RAIL_WIDTH,
  seedInitialState,
} from "./helpers";
import { useBoardMutators } from "./hooks/useBoardMutators";
import { useDepGraph } from "./hooks/useDepGraph";
import { useInlineAdd } from "./hooks/useInlineAdd";
import { usePullPlanDraft } from "./hooks/usePullPlanDraft";
import { useScopeMembers } from "./hooks/useScopeMembers";
import { useSelection } from "./hooks/useSelection";
import { useTrainHandlers } from "./hooks/useTrainHandlers";
import { useUndo } from "./hooks/useUndo";
import { useViewState } from "./hooks/useViewState";
import { useWorkCodePalette } from "./hooks/useWorkCodePalette";
import "./PullPlanGrid.css";

export type { ProtoNewAct, SeveredTieFlag, AnchorPublishStats } from "./state";

interface Props {
  context: PullPlanSectionContext;
  scheduleId: number;
  /** The Takt_SectionName this grid edits. Drives the unified dep-editor graph
   *  endpoint. When omitted (e.g. a merged multi-section pull with no single
   *  Takt section), the Edit Dependencies control is hidden. */
  depEditorSection?: string;
  /** When set, the grid autosaves rows + instances to the matching pull_plans
   * row via PUT on a debounced timer. */
  draftId?: number | null;
  /** Browse mode (true) renders the grid non-interactive: no bar drag, no
   * resize, no row dropdown edits, no palette drop. */
  readOnly?: boolean;
  /** Rendered at the far left of the ONE header row (the host's Back button)
   *  and at its far right (Discard / Publish cluster) — the workspace owns a
   *  single slim header instead of stacking the host's bar above its own. */
  headerLeft?: React.ReactNode;
  headerRight?: React.ReactNode;
  /** Discard the draft — invoked from the title menu ("{name} DRAFT ▾"). The
   *  host owns the confirm modal; this just opens it. */
  onDiscardDraft?: () => void;
  /** Anchor review status (outside ties total / not yet reviewed / severed) —
   *  surfaced by the host on the Publish confirmation. */
  onAnchorStatsChange?: (stats: AnchorPublishStats) => void;
  /** The live staged dep graph, lifted so the review page can render the same
   *  grouped Anchors view (read-only) off it. */
  onDepGraphChange?: (graph: DepGraph | null) => void;
  /** A frozen copy of the scope ledger (members + per-row facts), lifted so the
   *  host can render the same table in the publish modal's Diff tab. Fires
   *  whenever the staged draft changes the ledger. */
  onLedgerSnapshotChange?: (snap: LedgerSnapshot) => void;
  /** Nonce-stamped request to switch view (the publish warning's "review them
   *  now" link jumps to Anchors). */
  viewSignal?: { view: "board" | "sequence" | "anchors"; nonce: number } | null;
}

const PullPlanGrid: React.FC<Props> = ({
  context,
  scheduleId,
  depEditorSection,
  draftId = null,
  readOnly = false,
  headerLeft,
  headerRight,
  onDiscardDraft,
  onAnchorStatsChange,
  onDepGraphChange,
  onLedgerSnapshotChange,
  viewSignal = null,
}) => {
  const calendar = context.calendar;

  const initial = React.useMemo(() => seedInitialState(context), [context]);
  const [rows, setRows] = React.useState<ZoneRow[]>(initial.rows);
  const [instances, setInstances] = React.useState<ActivityInstance[]>(initial.instances);
  // Palette chip order: LAST-ADDED FIRST, persisted in the draft. Codes not
  // listed sort after, alphabetical. Owned here (not the palette hook) because
  // it rides in the undo snapshot + autosave payload, both declared above the
  // hook call.
  const [paletteOrder, setPaletteOrder] = React.useState<string[]>([]);
  const gridBodyRef = React.useRef<HTMLDivElement | null>(null);
  const gridScrollRef = React.useRef<HTMLDivElement | null>(null);
  // Undo history: every action snapshots the COMPLETE editable state before
  // running (staged ops + all board state), labeled for the history panel.
  const [draggingActivityKey, setDraggingActivityKey] = React.useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = React.useState(false);

  const {
    view,
    setView,
    paneMode,
    setPaneMode,
    focusedLeaf,
    setFocusedLeaf,
    trainTiesView,
    setTrainTiesView,
    seqMode,
    setSeqMode,
    seqHold,
    setSeqHold,
    trainSeqMode,
    setTrainSeqMode,
    selectedLane,
    setSelectedLane,
    goTab,
  } = useViewState(viewSignal);
  // Which panes the unified workspace shows. `view === "anchors"` is the only
  // separate tab; otherwise the board (top) and gantt (bottom) stack, gated by
  // paneMode (Board / Gantt / Both).
  const boardVisible = view !== "anchors" && paneMode !== "gantt";
  const ganttVisible = view !== "anchors" && paneMode !== "board";
  // Full-page logic: the gantt grip was dragged up past the board, so the logic
  // editor covers the train band too. Only meaningful in "both" (the grip lives
  // on the splitter); reset whenever the split layout isn't showing.
  const [logicCovered, setLogicCovered] = React.useState(false);
  React.useEffect(() => {
    if (paneMode !== "both" || view === "anchors") setLogicCovered(false);
  }, [paneMode, view]);
  // Full-screen the logic editor over the entire app (the toolbar's ⤢ toggle).
  const [logicFullscreen, setLogicFullscreen] = React.useState(false);
  const toggleLogicFullscreen = React.useCallback(
    () => setLogicFullscreen((v) => !v),
    [],
  );
  React.useEffect(() => {
    if (view === "anchors") setLogicFullscreen(false);
  }, [view]);

  // Outside ties (anchors) the user has marked reviewed — "predId>succId" keys,
  // persisted in the draft payload so the review survives reloads.
  const [reviewedAnchors, setReviewedAnchors] = React.useState<Set<string>>(
    () => new Set(),
  );
  const toggleAnchorReview = React.useCallback((key: string) => {
    setReviewedAnchors((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  // The delete guard: which activities are pending deletion + every dependency
  // tie that dies with them (outside ties demand explicit acknowledgement),
  // plus ties to kept/undecided scope members the delete would leave behind.
  const [deleteImpact, setDeleteImpact] = React.useState<{
    graphIds: string[];
    names: string[];
    ties: DeleteImpactTie[];
    orphans: InScopeOrphan[];
  } | null>(null);

  // --- Scope members (the To-pull lane + Plan scope popover) -------------- //
  // The committed membership truth for the plan's WBS leaves. The leaf set
  // comes from the draft payload's scope_wbs_ids (explicit from birth), with a
  // name-derived fallback for pre-scope drafts and browse mode.
  const [scopeMembers, setScopeMembers] = React.useState<ScopeMember[] | null>(null);
  const [fallbackScopeIds, setFallbackScopeIds] = React.useState<number[]>([]);
  // "Placed 41 of 53 — 12 still need a decision" after Pull all remaining.

  // Staged draft ops (v2 publish payload). Edit actions append here instead of
  // committing live; they persist via autosave and are applied by the publish
  // engine. Held as the whole DraftPayload so every pending_* array round-trips
  // (capabilities are migrated one at a time — the unmigrated arrays must not
  // be dropped on save). The grid renders staged ops optimistically.
  const [pendingOps, setPendingOps] = React.useState<DraftPayload>(emptyDraftPayload());

  // Latest editable state, kept in a ref so pushUndo can snapshot it without a
  // dependency-array churn. The state pieces are replaced immutably, so storing
  // the current references IS a valid point-in-time snapshot. (Assigned below,
  // after every state piece is declared.)
  const snapStateRef = React.useRef<GridSnapshot | null>(null);
  // True once the user has made ANY edit to the active draft. Gates the draft
  // load so a late-resolving fetch can't clobber freshly-staged work.
  const userEditedRef = React.useRef(false);
  const squelchUndoRef = React.useRef(false);
  const restoreSnap = React.useCallback((snap: GridSnapshot) => {
    setRows(snap.rows);
    setInstances(snap.instances);
    setPendingOps(snap.pendingOps);
    setProtoSpans(snap.protoSpans);
    setProtoLanes(snap.protoLanes);
    setProtoNewActs(snap.protoNewActs);
    setProtoRowOverrides(snap.protoRowOverrides);
    setProtoLeafOrder(snap.protoLeafOrder);
    setProtoExtraRows(snap.protoExtraRows);
    setProtoExtraLanes(snap.protoExtraLanes);
    setPaletteOrder(snap.paletteOrder);
    setReviewedAnchors(snap.reviewedAnchors);
    setLeafLevelOverrides(snap.leafLevelOverrides);
    setNotaktLeaves(snap.notaktLeaves);
    setProtoTrain(snap.protoTrain);
    setProtoSelection(new Set());
    // setState setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { historyItems, undoCount, pushUndo, undo, jumpToHistory, resetHistory } =
    useUndo<GridSnapshot>({ snapStateRef, restoreSnap, squelchUndoRef, userEditedRef });

  // Skip-flag for the autosave effect. Reset to true whenever something
  // server-driven mutates grid state (draft reset, draft load) so the resulting
  // state replace doesn't echo back as a PUT.
  const autosaveSkipFirstRef = React.useRef(true);
  const prevDraftIdRef = React.useRef<number | null>(draftId);

  // Seed the board to the committed state for this draft. The draft — not the
  // committed context — is the source of truth: once a draft is active
  // (draftId != null), a context REFETCH (e.g. the post-publish reload) must
  // NOT reset rows/instances/pendingOps; staged work lives only in board state.
  // It re-derives only via the `initial` memo (palette). We reset ONLY when the
  // draft itself changes, or when there's no active draft (browse mode) and the
  // committed context updates.
  React.useEffect(() => {
    const draftChanged = prevDraftIdRef.current !== draftId;
    prevDraftIdRef.current = draftId;
    if (!draftChanged && draftId != null) return;
    autosaveSkipFirstRef.current = true;
    userEditedRef.current = false;
    setRows(initial.rows);
    setInstances(initial.instances);
    resetHistory();
    setDepGraph(null);
    setPendingOps(emptyDraftPayload());
    inlineAdd.cancel();
    setReviewedAnchors(new Set());
    setLeafLevelOverrides(new Map());
    setNotaktLeaves(new Set());
    setProtoTrain(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, draftId]);

  // Calendar column for an ISO date (clamped; dates off the axis snap back).
  const calendarIndexFor = React.useCallback(
    (iso: string | null): number => indexOnAxis(calendar, iso),
    [calendar],
  );

  // Duration baselines for scope members pulled onto the board mid-draft —
  // their bars aren't in the seed, so buildEffectiveDraft needs the committed
  // span to derive a duration edit when such a bar is resized.
  const memberDurationBaselines = React.useMemo(() => {
    const m = new Map<number, number>();
    for (const mem of scopeMembers ?? []) {
      m.set(mem.activity_id, memberSpan(mem, calendarIndexFor).durationDays);
    }
    return m;
  }, [scopeMembers, calendarIndexFor]);

  // The draft exactly as publish sees it: staged pending_* ops PLUS the arrays
  // derived from live board geometry (committed-bar duration edits, zone order
  // from row order, new-activity duration/zone reconciled from their bar). One
  // builder feeds BOTH the autosave PUT and the dep-graph preview, so what you
  // preview is what's saved and committed.
  const effectiveDraft = React.useMemo<DraftPayload>(
    () =>
      buildEffectiveDraft(
        pendingOps,
        rows,
        instances,
        initial.rows,
        initial.instances,
        memberDurationBaselines,
      ),
    [pendingOps, rows, instances, initial.rows, initial.instances, memberDurationBaselines],
  );

  // PROTOTYPE board state that persists in the draft payload (declared above
  // the autosave/load effects that read it).
  // The plan window is set ONCE from the baseline (the scope's committed dates
  // when the pull plan starts) and never recomputed: the board starts at the
  // baseline's first early start, and the dashed line marks its latest early
  // finish.
  const [protoPlanWindow, setProtoPlanWindow] = React.useState<
    { start: string; end: string } | null
  >(null);
  const [protoNewActs, setProtoNewActs] = React.useState<ProtoNewAct[]>([]);
  const [protoLeafOrder, setProtoLeafOrder] = React.useState<string[] | null>(null);
  // Bars moved to a different row (activity id -> leaf key) + user-added rows.
  const [protoRowOverrides, setProtoRowOverrides] = React.useState<Map<number, string>>(
    () => new Map(),
  );
  const [protoExtraRows, setProtoExtraRows] = React.useState<string[]>([]);
  const [protoExtraLanes, setProtoExtraLanes] = React.useState<Map<string, number>>(
    () => new Map(),
  );
  const [protoSpans, setProtoSpans] = React.useState<Map<number, BarSpan>>(
    () => new Map(),
  );
  const [protoLanes, setProtoLanes] = React.useState<Map<number, number>>(
    () => new Map(),
  );
  // WBS-leaf LEVEL overrides (leaf key -> Takt_C the user chose) and leaves
  // flagged "not Takt work" (sequence-only). Both ride in the opaque payload
  // under proto_board.leaf_levels / proto_board.notakt_leaves.
  const [leafLevelOverrides, setLeafLevelOverrides] = React.useState<Map<string, string>>(
    () => new Map(),
  );
  const [notaktLeaves, setNotaktLeaves] = React.useState<Set<string>>(() => new Set());
  // The Takt train (user-authored canonical sequence), an opaque top-level
  // `train` payload key. Null until the user creates one.
  const [protoTrain, setProtoTrain] = React.useState<TrainState | null>(null);
  // A FRESH train armed for placement: the board takes a grab cursor + level
  // highlight, and a board click drops the run onto that level. Reset whenever
  // the train goes away or stops being fresh.
  const [placeArmed, setPlaceArmed] = React.useState(false);
  React.useEffect(() => {
    if (!protoTrain || protoTrain.kind !== "fresh") setPlaceArmed(false);
  }, [protoTrain]);
  // The train signature captured at the last Apply — the Apply button shows a ✓
  // "done" state while it still matches the current train, clearing on any edit.
  const [trainAppliedSig, setTrainAppliedSig] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!protoTrain) setTrainAppliedSig(null);
  }, [protoTrain]);

  React.useEffect(() => {
    if (protoPlanWindow || !scopeMembers || scopeMembers.length === 0) return;
    let start: string | null = null;
    let end: string | null = null;
    for (const m of scopeMembers) {
      if (m.restart_date && (start == null || m.restart_date < start)) start = m.restart_date;
      if (m.reend_date && (end == null || m.reend_date > end)) end = m.reend_date;
    }
    if (start) setProtoPlanWindow({ start, end: end ?? start });
  }, [protoPlanWindow, scopeMembers]);

  // The board day axis spans the FULL committed calendar, which begins at the
  // data date. Anchoring it there — rather than trimming to the first scheduled
  // bar — means a forward pass that re-floors work back toward the data date can
  // never land a bar off the left edge; it just scrolls into view. The board
  // opens scrolled to the first activity (the initial-scroll effect below), so
  // the data-date pre-roll sits off-screen-left until you scroll to it.
  const protoCalendar = calendar;
  const protoCalendarIndexFor = calendarIndexFor;
  const protoBaselineEndIdx = React.useMemo(() => {
    if (!protoPlanWindow) return null;
    const end = protoPlanWindow.end.slice(0, 10);
    let idx: number | null = null;
    for (let i = 0; i < protoCalendar.length; i++) {
      if (protoCalendar[i] <= end) idx = i;
      else break;
    }
    return idx;
  }, [protoCalendar, protoPlanWindow]);
  const protoGridWidth = LANE_RAIL_WIDTH + protoCalendar.length * DAY_PX;

  // Open the board scrolled to the first scheduled activity (the plan-window
  // start), not the data date — the axis reaches back to the data date so the
  // forward pass can place work there, but that pre-roll stays off-screen-left
  // until you scroll to it. Fires once per board (re-armed when the draft
  // changes); manual scrolling afterward is preserved.
  const scrolledDraftRef = React.useRef<number | "none" | null>(null);
  React.useLayoutEffect(() => {
    const el = gridScrollRef.current;
    if (!el || !protoPlanWindow) return;
    const key = draftId ?? "none";
    if (scrolledDraftRef.current === key) return;
    const idx = calendar.findIndex((d) => d >= protoPlanWindow.start.slice(0, 10));
    scrolledDraftRef.current = key;
    el.scrollLeft = Math.max(0, idx) * DAY_PX;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protoPlanWindow, draftId, calendar]);

  // Unified dep-editor graph — the flat, already-cascaded scheduled graph the
  // backend builds from the live committed schedule + this draft's pending_*
  // ops. The host owns it: re-fetched (debounced) on open and after every edit
  // intent, then handed to the presentational modal. The graph holds the union
  // of committed + draft edges with dates cascaded, so the modal derives rows /
  // bars / arrows directly and never recomputes logic.
  const {
    depGraph,
    setDepGraph,
    depGraphLoading,
    depGraphError,
    depGraphRef,
    reflectGraphDatesToBoard,
    pendingApplyDatesRef,
    handleAddEdge,
    handleRemoveEdge,
    handleRetypeEdge,
    handleReplaceEdge,
  } = useDepGraph({
    scheduleId,
    depEditorSection,
    readOnly,
    effectiveDraft,
    view,
    paneMode,
    seqHold,
    calendar,
    protoCalendarIndexFor,
    protoNewActs,
    pushUndo,
    setPendingOps,
    setInstances,
    setProtoSpans,
    setProtoNewActs,
  });

  // The complete point-in-time snapshot pushUndo captures.
  snapStateRef.current = {
    rows,
    instances,
    pendingOps,
    protoSpans,
    protoLanes,
    protoNewActs,
    protoRowOverrides,
    protoLeafOrder,
    protoExtraRows,
    protoExtraLanes,
    paletteOrder,
    reviewedAnchors,
    leafLevelOverrides,
    notaktLeaves,
    protoTrain,
  };

  // The entry flow's pull-target decision (display only on the board).
  const [pullTarget, setPullTarget] = React.useState<PullTargetChoice | null>(null);

  const { saveState, retrySave } = usePullPlanDraft({
    scheduleId,
    draftId,
    readOnly,
    pendingOps,
    setPendingOps,
    effectiveDraft,
    rows,
    instances,
    reviewedAnchors,
    protoNewActs,
    paletteOrder,
    protoLeafOrder,
    protoPlanWindow,
    protoExtraRows,
    protoRowOverrides,
    protoExtraLanes,
    protoSpans,
    protoLanes,
    leafLevelOverrides,
    notaktLeaves,
    protoTrain,
    userEditedRef,
    autosaveSkipFirstRef,
    prevDraftIdRef,
    resetHistory,
    setRows,
    setInstances,
    setReviewedAnchors,
    setProtoNewActs,
    setPaletteOrder,
    setProtoLeafOrder,
    setProtoPlanWindow,
    setProtoExtraRows,
    setProtoSpans,
    setProtoExtraLanes,
    setProtoLanes,
    setProtoRowOverrides,
    setLeafLevelOverrides,
    setNotaktLeaves,
    setProtoTrain,
    setPullTarget,
  });

  // Out-of-section tie search for the dep editor's "+ add" popover: the
  // server-side activity search minus the section's own in-scope work (already
  // offered as in-section candidates). Picking a result stages a tie to it.
  const fetchExternals = React.useCallback(
    async (q: string): Promise<ExternalCandidate[]> => {
      const results = await searchActivities(scheduleId, q, 25);
      const g = depGraphRef.current;
      const inScope = new Set(
        g ? Object.values(g.activities).filter((a) => a.in_scope).map((a) => a.id) : [],
      );
      return results
        .filter((r) => !inScope.has(String(r.id)))
        .map((r) => ({
          externalActivityId: r.id,
          taskCode: r.task_code,
          taskName: r.prefix || r.task_name,
          workCode: r.work_code,
          wbsName: r.section_name,
          startIso: r.start,
          finishIso: r.finish,
        }));
    },
    // depGraphRef is a stable ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduleId],
  );

  // ---------------------------------------------------------------------- //
  // Sequence-surface edits (duration / rename / add / chain / repeat / delete)
  // ---------------------------------------------------------------------- //

  // Duration edit from the sequence table. A bar-backed activity edits its bar
  // (buildEffectiveDraft derives the pending op); an unzoned one stages the op
  // directly. New activities always have a bar (we create one on add when they
  // get a zone+code) — their entry reconciles from it.
  const handleSeqEditDuration = React.useCallback(
    (graphId: string, days: number) => {
      pushUndo();
      const byCommitted = instances.find(
        (i) => i.source_activity_id != null && String(i.source_activity_id) === graphId,
      );
      const byDraft = instances.find((i) => i.draftId === graphId);
      const inst = byCommitted ?? byDraft;
      if (inst) {
        setInstances((prev) =>
          prev.map((i) =>
            i.instanceId === inst.instanceId ? { ...i, duration_days: days } : i,
          ),
        );
        // Keep the staged log in lock-step with the bar so a later edit that
        // returns the bar to baseline supersedes this value instead of leaving
        // a stale staged edit behind.
        if (inst.source_activity_id != null) {
          const aid = inst.source_activity_id;
          setPendingOps((prev) => stageDurationEdit(prev, aid, days));
        }
        return;
      }
      const numeric = Number(graphId);
      if (Number.isFinite(numeric) && depGraphRef.current?.activities[graphId]?.is_new !== true) {
        setPendingOps((prev) => stageDurationEdit(prev, numeric, days));
      } else {
        // A zone-less new activity: patch its staged entry directly.
        setPendingOps((prev) => ({
          ...prev,
          pending_new_activities: prev.pending_new_activities.map((n) =>
            n.draft_id === graphId ? { ...n, duration_days: days } : n,
          ),
        }));
      }
    },
    // depGraphRef is a stable ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instances, pushUndo],
  );

  // Apply an edit-in-place (✎) — code / name / days for ANY in-scope activity.
  // Draft-added ones patch their staged entry; committed ones stage a rename /
  // duration edit / re-code. Bar cosmetics follow immediately.
  const handleApplyEdit = React.useCallback(
    (id: string, args: ApplyEditArgs) => {
      const g = depGraphRef.current;
      const node = g?.activities[id];
      if (!node) return;
      pushUndo();

      const isNew = node.is_new;
      const numericId = Number(id);
      const code = args.code || null;
      const nameChanged = args.name !== node.name;
      const daysChanged = args.days !== node.duration_days;
      const codeChanged = (code ?? "") !== (node.work_code ?? "");

      const codeAdd: PendingWorkCodeAdd | null =
        code && !paletteByKey.has(code)
          ? {
              code,
              prefix: args.codeName || code,
              label: args.codeName || code,
              color: args.codeColor || hashColor(code),
              default_duration_days: args.days,
            }
          : null;

      setPendingOps((prev) => {
        let next = codeAdd ? stageTaktifications(prev, [], [codeAdd]) : prev;
        if (isNew) {
          next = {
            ...next,
            pending_new_activities: next.pending_new_activities.map((n) =>
              n.draft_id === id
                ? { ...n, name: args.name, duration_days: args.days, work_code: code }
                : n,
            ),
          };
        } else {
          if (nameChanged) next = stageRenameEdit(next, numericId, args.name);
          if (codeChanged && code) {
            next = stageRezone(next, [
              {
                activity_id: numericId,
                takt_work: code,
                takt_a: node.zone.a || null,
                takt_b: node.zone.b || null,
                takt_c: node.zone.c || null,
              },
            ]);
          } else if (codeChanged && !code) {
            next = removeFromWorkCode(next, numericId);
          }
          // Always stage the duration (bar-backed or not) so the staged log and
          // the bar's geometry never disagree — a stale staged edit must not be
          // able to outlive a later edit back to baseline.
          if (daysChanged) next = stageDurationEdit(next, numericId, args.days);
        }
        return next;
      });

      // Bar cosmetics: color/code + duration follow the edit immediately.
      setInstances((prev) =>
        prev.map((i) => {
          const gid =
            i.source_activity_id != null ? String(i.source_activity_id) : i.draftId;
          if (gid !== id) return i;
          let next = i;
          if (daysChanged) next = { ...next, duration_days: args.days };
          if (codeChanged && code) next = { ...next, activityKey: code };
          return next;
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instances, pushUndo],
  );


  // ---- Scope membership (the To-pull lane's truth) ----------------------- //

  // Pre-scope drafts and browse mode have no scope_wbs_ids in the payload —
  // fall back to the section's name-derived leaves.
  React.useEffect(() => {
    if (!depEditorSection) return;
    if (pendingOps.scope_wbs_ids.length > 0) return;
    let cancelled = false;
    fetchTaktSectionWbsNodeIds(scheduleId, depEditorSection)
      .then((ids) => {
        if (!cancelled) setFallbackScopeIds(ids);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [scheduleId, depEditorSection, pendingOps.scope_wbs_ids.length]);

  const scopeWbsIds = React.useMemo<number[]>(
    () =>
      pendingOps.scope_wbs_ids.length > 0
        ? pendingOps.scope_wbs_ids
        : fallbackScopeIds,
    [pendingOps.scope_wbs_ids, fallbackScopeIds],
  );

  const scopeIdsKey = scopeWbsIds.join(",");

  const sectionClaims = React.useMemo(
    () => new Set(pendingOps.pending_section_claims.map((c) => c.activity_id)),
    [pendingOps.pending_section_claims],
  );

  // WBS-leaf LEVEL pills: only active for a Takt-section plan (a plain plan
  // has no Takt level to set).
  const levelUiActive = Boolean(depEditorSection);

  const {
    memberStateById,
    protoMembers,
    allScopeLeaves,
    leafTaktByLeaf,
    effectiveLevelByLeaf,
    miscodeCountByLeaf,
    targetByLeaf,
    handleSetLeafLevel,
    handleToggleNotakt,
  } = useScopeMembers({
    scheduleId,
    context,
    depEditorSection,
    scopeMembers,
    setScopeMembers,
    scopeIdsKey,
    pendingOps,
    setPendingOps,
    instances,
    protoNewActs,
    protoCalendar,
    protoRowOverrides,
    protoExtraRows,
    leafLevelOverrides,
    setLeafLevelOverrides,
    notaktLeaves,
    setNotaktLeaves,
    levelUiActive,
    pullTarget,
    pushUndo,
  });

  const memberNamesById = React.useMemo(() => {
    const m = new Map<number, string>();
    for (const mem of scopeMembers ?? []) m.set(mem.activity_id, mem.task_name);
    return m;
  }, [scopeMembers]);

  // ---- The delete guard ------------------------------------------------- //

  // Every tie that dies if `idSet` is deleted, with the SURVIVING endpoint
  // described. A survivor outside the plan (another section / finished work)
  // is the dangerous kind — the user must acknowledge severing it.
  const buildDeleteTies = React.useCallback(
    (g: DepGraph, idSet: Set<string>): DeleteImpactTie[] => {
      const ties: DeleteImpactTie[] = [];
      for (const [succId, edges] of Object.entries(g.edges)) {
        for (const e of edges) {
          const succDeleted = idSet.has(succId);
          const predDeleted = idSet.has(e.pred_id);
          if (succDeleted === predDeleted) continue; // untouched, or dies wholly inside the deletion
          const survivorId = succDeleted ? e.pred_id : succId;
          const survivor = g.activities[survivorId];
          if (!survivor) continue;
          const outside = !survivor.in_scope || nodeStatus(survivor) === "complete";
          ties.push({
            otherLabel: activityLabel(survivor),
            otherTaskCode: survivor.task_code,
            origin: outside
              ? nodeStatus(survivor) === "complete"
                ? "finished work"
                : survivor.section || "another section"
              : "",
            direction: succDeleted ? "pred" : "succ",
            rel: `${e.type}${formatLagSuffix(e.lag_hours)}`,
            outside,
          });
        }
      }
      // Outside ties first — they're the ones that matter.
      return ties.sort((a, b) => Number(b.outside) - Number(a.outside));
    },
    [],
  );

  // Open the guard for a set of graph ids. Fetches the graph on demand (board
  // deletes can happen before the sequence view ever populated it).
  const requestDelete = React.useCallback(
    async (graphIds: string[]) => {
      if (graphIds.length === 0) return;
      let g = depGraphRef.current;
      if (!g && depEditorSection) {
        try {
          g = await fetchDepEditorGraph(scheduleId, depEditorSection, effectiveDraft);
        } catch {
          g = null;
        }
      }
      const idSet = new Set(graphIds);
      const names = graphIds.map((id) => {
        const node = g?.activities[id];
        if (node) return activityLabel(node);
        const member = scopeMembers?.find((m) => String(m.activity_id) === id);
        if (member) return member.task_name;
        const inst = instances.find(
          (i) =>
            (i.source_activity_id != null && String(i.source_activity_id) === id) ||
            i.draftId === id,
        );
        return inst ? paletteByKey.get(inst.activityKey)?.name ?? inst.activityKey : id;
      });
      setDeleteImpact({
        graphIds,
        names,
        ties: g ? buildDeleteTies(g, idSet) : [],
        orphans: g
          ? buildInScopeOrphans(g, idSet, memberStateById, memberNamesById)
          : [],
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduleId, depEditorSection, effectiveDraft, instances, buildDeleteTies, scopeMembers, memberStateById, memberNamesById],
  );

  // Confirmed: drop the bars + stage the deletions (committed ids hard-delete
  // on publish; draft-added ones just drop their staged entry).
  const confirmDelete = React.useCallback(() => {
    if (!deleteImpact) return;
    // labeled push below replaces the bare one
    const idSet = new Set(deleteImpact.graphIds);
    const g = depGraphRef.current;
    const existingIds: number[] = [];
    const draftIds: string[] = [];
    for (const id of deleteImpact.graphIds) {
      const node = g?.activities[id];
      if (node?.is_new) {
        draftIds.push(id);
      } else {
        const n = Number(id);
        if (Number.isFinite(n)) existingIds.push(n);
        else draftIds.push(id);
      }
    }
    pushUndo(
      deleteImpact.graphIds.length > 1
        ? `Delete ${deleteImpact.graphIds.length}`
        : "Delete",
    );
    setInstances((prev) =>
      prev.filter((i) => {
        const gid =
          i.source_activity_id != null ? String(i.source_activity_id) : i.draftId;
        return gid == null || !idSet.has(gid);
      }),
    );
    // Resolve committed edge_ids to their endpoints so a staged remove/update of
    // an edge whose endpoint we're deleting is pruned too (else it orphans and
    // blocks the dep graph + publish).
    const edgeEndpoints = new Map<
      number,
      { pred: string | number; succ: string | number }
    >();
    if (g) {
      for (const [succId, edges] of Object.entries(g.edges)) {
        for (const e of edges) {
          if (e.edge_id != null) {
            edgeEndpoints.set(e.edge_id, { pred: e.pred_id, succ: succId });
          }
        }
      }
    }
    setPendingOps((prev) =>
      pruneScopeDecisions(
        deleteActivities(prev, existingIds, draftIds, edgeEndpoints),
        existingIds,
      ),
    );
    setDeleteImpact(null);
    // depGraphRef is a stable ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteImpact, pushUndo]);

  // Re-zone already-taktified activities from the legend pencil editor. Each
  // managed activity's bar moves to its final zone (the staged Takt_B/C if
  // changed, else its original); dates and the work code are untouched.
  const handleStageRezone = React.useCallback(
    (managedIds: number[], taktifications: PendingTaktification[]) => {
      pushUndo();
      setPendingOps((prev) => stageRezone(prev, taktifications));

      const taktById = new Map(taktifications.map((t) => [t.activity_id, t]));
      const actById = new Map(context.activities.map((a) => [a.id, a]));
      const targetZone = new Map<number, { a: string; b: string; c: string }>();
      for (const id of managedIds) {
        const t = taktById.get(id);
        const act = actById.get(id);
        if (t) {
          targetZone.set(id, {
            a: t.takt_a ?? act?.takt_a ?? "",
            b: t.takt_b ?? "",
            c: t.takt_c ?? "",
          });
        } else if (act) {
          targetZone.set(id, { a: act.takt_a, b: act.takt_b, c: act.takt_c });
        }
      }

      const { rows: nextRows, rowIdByZone } = ensureZoneRows(
        rows,
        Array.from(targetZone.values()).map((z) => ({
          takt_a: z.a,
          takt_b: z.b,
          takt_c: z.c,
        })),
      );
      const nextInstances = instances.map((inst) => {
        if (inst.source_activity_id == null) return inst;
        const z = targetZone.get(inst.source_activity_id);
        if (!z) return inst;
        const rowId = rowIdByZone.get(foldedZoneKey(z.a, z.b, z.c));
        return rowId && rowId !== inst.rowId ? { ...inst, rowId } : inst;
      });

      if (nextRows.length > rows.length) setRows(nextRows);
      setInstances(nextInstances);
    },
    [rows, instances, context.activities, pushUndo],
  );

  // Drop every board bar sourced from one of `ids` (an activity full-untagged
  // off the code) so it leaves the grid for the untagged palette immediately.
  const dropInstancesFor = React.useCallback(
    (ids: Set<number>) =>
      setInstances((prev) =>
        prev.filter((i) => i.source_activity_id == null || !ids.has(i.source_activity_id)),
      ),
    [],
  );

  const {
    palette,
    paletteByKey,
    visiblePalette,
    codeDisplay,
    effectiveCodeColors,
    codeEditTarget,
    setCodeEditTarget,
    codeEditActivities,
    handleAddCodeToPalette,
    handleEditWorkCode,
    handleDeleteCode,
    handleRemoveFromCode,
  } = useWorkCodePalette({
    context,
    initial,
    pendingOps,
    setPendingOps,
    instances,
    setInstances,
    protoMembers,
    pushUndo,
    dropInstancesFor,
    paletteOrder,
    setPaletteOrder,
  });

  const inlineAdd = useInlineAdd({
    rows,
    setRows,
    setInstances,
    setPendingOps,
    pendingNewActivities: pendingOps.pending_new_activities,
    paletteByKey,
    calendarIndexFor,
    depGraphRef,
    pushUndo,
    onHold: setSeqHold,
  });

  // ---------------------------------------------------------------------- //
  // Bulk selection (multi-select → bulk delete / duplicate)                //
  // ---------------------------------------------------------------------- //

  // This section's palette as work-code records, so the inline add row's
  // picker surfaces draft-staged codes ahead of the backend search results.
  const paletteAsExistingCodes = React.useMemo<ExistingWorkCode[]>(() => {
    const countByKey = new Map<string, number>();
    for (const i of instances) {
      countByKey.set(i.activityKey, (countByKey.get(i.activityKey) ?? 0) + 1);
    }
    return palette.map((p) => ({
      code: p.activityKey,
      prefix: p.name,
      color: p.color,
      activity_count: countByKey.get(p.activityKey) ?? 0,
      sections: [context.section.name],
    }));
  }, [palette, instances, context.section.name]);

  // Work code -> display name (what the board labels bars with), so the train
  // ties show "Frame Ceilings", not just "99".
  const codeNameByKey = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of palette) if (p.name) m[p.activityKey] = p.name;
    return m;
  }, [palette]);

  // Board level tuples (board order) — the dep table renders empty levels too.
  const boardZones = React.useMemo(
    () => rows.map((r) => ({ a: r.takt_a, b: r.takt_b, c: r.takt_c })),
    [rows],
  );

  // Names of staged adds for the editor's "after …" chip while the held pass
  // hasn't surfaced them in the graph yet. Ref so the config memo stays stable.
  const pendingNamesRef = React.useRef<Map<string, string>>(new Map());
  pendingNamesRef.current = new Map(
    pendingOps.pending_new_activities.map((n) => [n.draft_id, n.name]),
  );

  // Board context for auto-placing a new inline-added activity — read at commit
  // time (via the ref) so volatile board state doesn't churn the commit callback.
  const placementCtxRef = React.useRef<{
    leafInfoByGid: Map<string, LeafInfo>;
    boardDatesByGid: Map<string, { start: string | null; finish: string | null }>;
    calendarLength: number;
    dataDate: string | null;
    fallbackLeaf: LeafInfo | null;
  }>({ leafInfoByGid: new Map(), boardDatesByGid: new Map(), calendarLength: 0, dataDate: null, fallbackLeaf: null });

  // Commit an inline add: stage it (codes/ties/pending op) AND give it a real
  // board bar at an auto-computed start (the workday after its latest pred's
  // board finish, or the data date) so it shows on the board and in the logic
  // immediately — without a forward pass and without moving anything else.
  const inlineAddCommit = inlineAdd.commit;
  const commitInlineAdd = React.useCallback(
    (args: Parameters<typeof inlineAddCommit>[0]): string => {
      const draftId = inlineAddCommit(args);
      const ctx = placementCtxRef.current;
      const placement = computeInlineAddPlacement({
        predIds: args.predIds,
        anchorId: args.positionAfterId,
        durationDays: args.days,
        leafOfGid: (id) => ctx.leafInfoByGid.get(id) ?? null,
        boardFinishOf: (id) => ctx.boardDatesByGid.get(id)?.finish ?? null,
        calendarIndexFor: protoCalendarIndexFor,
        calendarLength: ctx.calendarLength,
        dataDate: ctx.dataDate,
        fallbackLeaf: ctx.fallbackLeaf,
      });
      setProtoNewActs((prev) => [
        ...prev,
        {
          draftId,
          wbsKey: placement.wbsKey,
          wbsPath: placement.wbsPath,
          wbsNodeId: placement.wbsNodeId,
          startIndex: placement.startIndex,
          durationDays: Math.max(1, Math.round(args.days) || 1),
          code: args.code,
          name: args.name,
        },
      ]);
      return draftId;
    },
    [inlineAddCommit, protoCalendarIndexFor, setProtoNewActs],
  );

  const inlineAddConfig = React.useMemo(
    () => ({
      scheduleId,
      extraCodes: paletteAsExistingCodes,
      onCommit: commitInlineAdd,
      localNameFor: (id: string) => pendingNamesRef.current.get(id) ?? null,
    }),
    [scheduleId, paletteAsExistingCodes, commitInlineAdd],
  );

  // ---------------------------------------------------------------------- //
  // Render                                                                 //
  // ---------------------------------------------------------------------- //

  const anchorStats = React.useMemo<AnchorPublishStats>(() => {
    if (!depGraph) return { total: 0, unreviewed: 0, severed: [] };
    const keys = collectAnchorKeys(depGraph);
    const unreviewed = keys.filter((k) => !reviewedAnchors.has(k)).length;
    const severed: SeveredTieFlag[] = (depGraph.severed_anchors ?? []).map((s) => {
      const ext = depGraph.activities[s.external_id];
      return {
        key: `${s.pred_id}->${s.succ_id}`,
        externalName: ext?.name ?? s.external_id,
        externalTaskCode: ext?.task_code ?? "",
        insideName: s.deleted_name,
        direction: s.direction,
        reason: s.reason ?? "delete",
      };
    });
    return { total: keys.length, unreviewed, severed };
  }, [depGraph, reviewedAnchors]);
  const anchorStatsChangeRef = React.useRef(onAnchorStatsChange);
  anchorStatsChangeRef.current = onAnchorStatsChange;
  React.useEffect(() => {
    anchorStatsChangeRef.current?.(anchorStats);
  }, [anchorStats]);
  const depGraphChangeRef = React.useRef(onDepGraphChange);
  depGraphChangeRef.current = onDepGraphChange;
  React.useEffect(() => {
    depGraphChangeRef.current?.(depGraph);
  }, [depGraph]);

  // The Anchors tab shows only once the draft has actually changed an anchor —
  // deleted an in-section endpoint of an outside tie, or cut such a tie. Not
  // tied to the adjust/re-pull fork anymore: a plain edit plan that severs an
  // anchor still needs to re-home it. No change ⇒ no tab.
  const anchorsHasChange = anchorStats.severed.length > 0;
  React.useEffect(() => {
    if (view === "anchors" && !anchorsHasChange) setView("board");
    // setView setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, anchorsHasChange]);

  // PROTOTYPE: per-activity anchor direction — an in-scope activity tied to a
  // driver (outside / finished work). "in" = the driver feeds it, "out" = it
  // feeds the driver.
  const anchorDirById = React.useMemo(() => {
    const dirs = new Map<number, AnchorDir>();
    if (!depGraph) return dirs;
    const isDriverNode = (id: string) => {
      const n = depGraph.activities[id];
      return n != null && (!n.in_scope || nodeStatus(n) === "complete");
    };
    const mark = (id: string, dir: "in" | "out") => {
      const aid = Number(id);
      if (!Number.isFinite(aid)) return;
      const prev = dirs.get(aid);
      dirs.set(aid, prev && prev !== dir ? "both" : prev ?? dir);
    };
    for (const [succId, edges] of Object.entries(depGraph.edges)) {
      for (const e of edges) {
        const predDriver = isDriverNode(e.pred_id);
        const succDriver = isDriverNode(succId);
        if (predDriver === succDriver) continue;
        if (predDriver) mark(succId, "in");
        else mark(e.pred_id, "out");
      }
    }
    return dirs;
  }, [depGraph]);

  // PROTOTYPE: chip-drops on the WBS board stage a new activity. Each entry
  // mirrors its pending_new_activities row (same draft_id) plus the leaf/day
  // the bar renders at. Synthetic negative activity ids key the board/lane.

  const removeProtoNewMany = React.useCallback((activityIds: number[]) => {
    setProtoNewActs((prev) => {
      const drop = new Set(
        activityIds.map((aid) => prev[-aid - 1]?.draftId).filter(Boolean),
      );
      if (drop.size === 0) return prev;
      setPendingOps((ops) => ({
        ...ops,
        pending_new_activities: ops.pending_new_activities.filter(
          (n) => !drop.has(n.draft_id),
        ),
      }));
      // Tombstone instead of removing: later negative ids stay valid.
      return prev.map((n) => (drop.has(n.draftId) ? { ...n, deleted: true } : n));
    });
  }, []);
  const handleProtoRemoveNew = React.useCallback(
    (activityId: number) => removeProtoNewMany([activityId]),
    [removeProtoNewMany],
  );

  // A bulk-dragged selection was dropped → ask move vs duplicate.
  const [bulkDrop, setBulkDrop] = React.useState<{
    ids: number[];
    dxDays: number;
    leafKey: string | null;
    lane: number | null;
  } | null>(null);

  const {
    protoSpanOf,
    stageProtoDrop,
    handleProtoMove,
    handleProtoResize,
    handleProtoRecode,
    handleProtoRename,
    handleProtoDelete,
    pinLanes,
    deleteTrainMappedRefs,
    barGraphId,
    applyBulkMove,
    applyBulkDuplicate,
    handleBulkDrop,
  } = useBoardMutators({
    palette,
    protoMembers,
    scopeMembers,
    protoNewActs,
    protoSpans,
    protoCalendar,
    protoCalendarIndexFor,
    protoTrain,
    bulkDrop,
    setBulkDrop,
    setProtoTrain,
    setProtoNewActs,
    setPendingOps,
    setProtoSpans,
    setProtoLanes,
    setProtoRowOverrides,
    pushUndo,
    requestDelete,
    removeProtoNewMany,
    depGraphRef,
  });

  // Per-leaf ordered work codes (the same SelectionCar shape the manual "Create
  // train" path uses) — the input to train auto-detection. Reflects staged edits
  // via protoMembers, so detected trains follow the live board.
  const boardLeafCars = React.useMemo(() => {
    const map = new Map<string, SelectionCar[]>();
    for (const m of protoMembers) {
      const code = (m.takt_work ?? "").trim();
      if (!code) continue;
      const ref =
        m.activity_id > 0
          ? String(m.activity_id)
          : protoNewActs[-m.activity_id - 1]?.draftId ?? String(m.activity_id);
      const span = protoSpanOf(m.activity_id);
      const key = (m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"]).join(" ");
      const car: SelectionCar = {
        ref,
        code,
        durationDays: span?.durationDays ?? m.original_duration ?? 1,
        startIndex: span?.startIndex ?? 0,
        locked: m.status === "TK_Complete" || m.status === "TK_Active",
        completed: m.status === "TK_Complete",
      };
      const list = map.get(key);
      if (list) list.push(car);
      else map.set(key, [car]);
    }
    map.forEach((cars) => cars.sort((a, b) => a.startIndex - b.startIndex));
    return map;
  }, [protoMembers, protoNewActs, protoSpanOf]);

  // Per-activity code + leaf lookups for the train-selection mechanics.
  const memberIndex = React.useMemo(() => {
    const code = new Map<number, string>();
    const leaf = new Map<number, string>();
    for (const m of protoMembers) {
      leaf.set(
        m.activity_id,
        (m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"]).join(" "),
      );
      const c = (m.takt_work ?? "").trim();
      if (c) code.set(m.activity_id, c);
    }
    return { code, leaf };
  }, [protoMembers]);

  // Board lane grouping: each committed activity → the committed activity it
  // follows Finish→Start (its latest-finishing FS predecessor), from the
  // forward-pass graph. Feeds the board so an FS run renders on one lane;
  // SS/FF/SF and untied work greedy-fills around it. Empty until the graph loads.
  // FS-chain lane grouping. Includes draft-added activities (translated to their
  // negative board id) so a JUST-ADDED bar joins its predecessor's lane instead
  // of starting its own.
  const boardFsLinks = React.useMemo(
    () => buildBoardFsLinks(depGraph, protoNewActs.map((n) => (n.deleted ? "" : n.draftId))),
    [depGraph, protoNewActs],
  );

  const { protoSelection, setProtoSelection, handleSelectionChange, protoBulkDelete } =
    useSelection({
      memberIndex,
      protoTrain,
      setProtoTrain,
      removeProtoNewMany,
      requestDelete,
      deleteTrainMappedRefs,
      pushUndo,
    });

  // Live ghost-preview: while a run is selected (and no train exists yet), the
  // bars on every OTHER level that contains the same set of codes light up, so
  // you see where "Create train" would land before clicking it.
  const orderedSelection = React.useCallback((): SelectionCar[] => {
    return Array.from(protoSelection)
      .filter((id) => !isTrainCarId(id))
      .map((id) => ({ id, span: protoSpanOf(id) }))
      .filter((x) => x.span && x.span.startIndex >= 0)
      .sort((a, b) => a.span!.startIndex - b.span!.startIndex || a.id - b.id)
      .map((x) => {
        const m = protoMembers.find((p) => p.activity_id === x.id);
        return {
          ref:
            x.id >= 0
              ? String(x.id)
              : protoNewActs[-x.id - 1]?.draftId ?? String(x.id),
          code: (m?.takt_work ?? "").trim(),
          durationDays: x.span!.durationDays,
          startIndex: x.span!.startIndex,
          locked: m?.status === "TK_Complete" || m?.status === "TK_Active",
          completed: m?.status === "TK_Complete",
        };
      });
  }, [protoSelection, protoSpanOf, protoMembers, protoNewActs]);

  const trainPreviewRefs = React.useMemo(() => {
    if (protoTrain || protoSelection.size < 2) return new Set<number>();
    let srcLeaf: string | null = null;
    protoSelection.forEach((id) => {
      const lf = memberIndex.leaf.get(id);
      if (lf) srcLeaf = lf;
    });
    return previewRefs(orderedSelection(), srcLeaf, boardLeafCars);
  }, [protoTrain, protoSelection, memberIndex, boardLeafCars, orderedSelection]);

  // PROTOTYPE board chrome: per-row Takt fields (start blank), row order,
  // collapsible takt column + work-code palette.
  const protoLabelWidth = LANE_RAIL_WIDTH;

  // Code dropped on a level that already carries it → ask move vs duplicate.
  const [dropConflict, setDropConflict] = React.useState<DropConflict | null>(null);

  const handleProtoDrop = React.useCallback(
    (leaf: { key: string; path: string[] }, dayIndex: number) => {
      if (readOnly || !draggingActivityKey) return;
      const code = draggingActivityKey;
      setDraggingActivityKey(null);
      const existing = protoMembers
        .filter(
          (m) =>
            m.activity_id > 0 &&
            m.status !== "TK_Complete" &&
            m.takt_work === code &&
            (m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"]).join(" ") === leaf.key,
        )
        .sort((a, b) => (a.restart_date ?? "").localeCompare(b.restart_date ?? ""));
      if (existing.length > 0) {
        setDropConflict({ leaf, dayIndex, code, existingId: existing[0].activity_id });
        return;
      }
      stageProtoDrop(leaf, dayIndex, code);
    },
    [readOnly, draggingActivityKey, protoMembers, stageProtoDrop],
  );

  const boardDatesByGid = React.useMemo(() => {
    const m = new Map<string, { start: string | null; finish: string | null }>();
    for (const mem of protoMembers) {
      const span = protoSpanOf(mem.activity_id);
      const gid =
        mem.activity_id >= 0
          ? String(mem.activity_id)
          : protoNewActs[-mem.activity_id - 1]?.draftId ?? String(mem.activity_id);
      if (span && span.startIndex >= 0) {
        m.set(gid, {
          start: protoCalendar[span.startIndex] ?? null,
          finish:
            protoCalendar[
              Math.min(span.startIndex + span.durationDays - 1, protoCalendar.length - 1)
            ] ?? null,
        });
      } else {
        m.set(gid, { start: mem.restart_date, finish: mem.reend_date });
      }
    }
    return m;
  }, [protoMembers, protoSpanOf, protoNewActs, protoCalendar]);

  // gid → its WBS leaf (committed members + draft-added bars), plus the leaf to
  // place into when an inline add has no anchor (the drilled-in level, else the
  // first). Feeds the inline-add auto-placement.
  const { leafInfoByGid, fallbackLeaf } = React.useMemo(() => {
    const byGid = new Map<string, LeafInfo>();
    const byKey = new Map<string, LeafInfo>();
    const note = (gid: string, info: LeafInfo) => {
      byGid.set(gid, info);
      if (!byKey.has(info.key)) byKey.set(info.key, info);
    };
    for (const m of scopeMembers ?? []) {
      const path = m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"];
      note(String(m.activity_id), { key: path.join(" "), path, nodeId: m.wbs_node_id });
    }
    for (const n of protoNewActs) {
      if (n.deleted) continue;
      note(n.draftId, { key: n.wbsKey, path: n.wbsPath, nodeId: n.wbsNodeId });
    }
    const firstLeaf: LeafInfo | null = Array.from(byKey.values())[0] ?? null;
    const fb = (focusedLeaf != null ? byKey.get(focusedLeaf) : undefined) ?? firstLeaf;
    return { leafInfoByGid: byGid, fallbackLeaf: fb };
  }, [scopeMembers, protoNewActs, focusedLeaf]);

  placementCtxRef.current = {
    leafInfoByGid,
    boardDatesByGid,
    calendarLength: protoCalendar.length,
    dataDate: depGraph?.data_date ?? null,
    fallbackLeaf,
  };

  // The staged inline adds, given the board dates their just-created bar carries
  // (keyed by draft id) so the logic renders them with real dates right away,
  // before the dep-graph refetch picks them up.
  const enrichedLocalAdds = React.useMemo(
    () =>
      inlineAdd.localAdds.map((a) => {
        const bd = boardDatesByGid.get(a.draftId);
        return bd ? { ...a, start: bd.start, finish: bd.finish } : a;
      }),
    [inlineAdd.localAdds, boardDatesByGid],
  );

  // Would "Apply dates to board" actually move anything? Greys the button out
  // when the board already matches the forward pass, lights it up when it doesn't.
  const applyDatesDirty = React.useMemo(
    () => (depGraph ? boardDatesDirty(depGraph, boardDatesByGid, protoCalendarIndexFor) : false),
    [depGraph, boardDatesByGid, protoCalendarIndexFor],
  );

  // The board is a SANDBOX: the forward pass never moves its bars. This is
  // the one explicit bridge — the Dependencies tab's computed dates land as
  // board positions only when the user clicks "Apply dates to board".
  const applyGraphDatesToBoard = React.useCallback(() => {
    if (!depGraphRef.current) return;
    pushUndo("Forward pass apply");
    reflectGraphDatesToBoard();
    setView("board");
    // depGraphRef is a stable ref; setView setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushUndo, reflectGraphDatesToBoard]);
  // The train track is a sandbox too: the forward pass (live mode) never moves
  // its cars. This bakes the forward-pass positions into the cars' hand-placed
  // startIndex and returns to board mode — the train mirror of "Apply dates to
  // board". One undoable step.
  const applyDatesToTrain = React.useCallback(() => {
    if (!protoTrain) return;
    const sim = simulateTrain(protoTrain);
    pushUndo("Forward pass apply (train)");
    setProtoTrain((t) =>
      t
        ? {
            ...t,
            cars: t.cars.map((c) => {
              const p = sim.get(c.carId);
              return p ? { ...c, startIndex: p.startDay } : c;
            }),
          }
        : t,
    );
    setTrainSeqMode("board");
    // setState setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protoTrain, pushUndo]);

  // Build a Takt train from the current board selection: one car per code, in
  // board order, chained FS. Pure authoring state (no pending ops staged).
  // Selected activities in board order, resolved to {ref, code, duration}.

  const {
    handleCreateTrain,
    handleClearTrain,
    trainLevelChips,
    handleRemoveTrainLevel,
    handleAddTrainLevel,
    trainCanAddLevel,
    handleApplyTrain,
    handleAddTrainDep,
    handleUpdateTrainDep,
    handleRemoveTrainDep,
    handleUpdateTrainCarDuration,
    handleRemoveTrainCars,
    trainGraphBundle,
    trainMappedRefs,
  } = useTrainHandlers({
    protoTrain,
    setProtoTrain,
    trainSeqMode,
    setTrainTiesView,
    setFocusedLeaf,
    setProtoSelection,
    protoSpanOf,
    deleteTrainMappedRefs,
    pendingApplyDatesRef,
    setProtoNewActs,
    setProtoSpans,
    setPendingOps,
    depGraphRef,
    orderedSelection,
    boardLeafCars,
    allScopeLeaves,
    scopeMembers,
    protoNewActs,
    codeNameByKey,
    effectiveCodeColors,
    protoCalendar,
    scheduleId,
    depEditorSection,
    effectiveDraft,
    pushUndo,
  });
  const trainGraph = trainGraphBundle?.graph ?? null;
  const trainBoardDates = trainGraphBundle?.boardDates;
  const trainSeqIntents = React.useMemo(
    () => ({
      onAddEdge: (predId: string, succId: string) =>
        handleAddTrainDep(predId, succId, "FS", 0),
      onRemoveEdge: (succId: string, predId: string) =>
        handleRemoveTrainDep(predId, succId),
      onRetypeEdge: (
        succId: string,
        predId: string,
        type: TrainDepType,
        lagDays: number,
      ) => handleUpdateTrainDep(predId, succId, type, lagDays),
      // Re-point a train tie atomically (drop old car-tie + add new), aborting
      // with an alert on a loop — never the silent remove-then-failed-add delete.
      onReplaceEdge: (
        removeSucc: string,
        removePred: string,
        addPred: string,
        addSucc: string,
        type: TrainDepType,
        lagDays: number,
      ) => {
        if (!protoTrain) return;
        const { deps, cycle } = replaceTrainDep(
          protoTrain.deps, removePred, removeSucc, addPred, addSucc, type, lagDays,
        );
        if (cycle) {
          notify("Can't move this tie — it would create a loop.", "error");
          return;
        }
        pushUndo("Re-point train tie");
        setProtoTrain((t) => (t ? { ...t, deps } : t));
      },
    }),
    [handleAddTrainDep, handleRemoveTrainDep, handleUpdateTrainDep, protoTrain, pushUndo],
  );

  // Bar-popover predecessor row for TRAIN-CAR bars: click a train element on the
  // board and set/replace its predecessor — but the search only offers the OTHER
  // train elements (instanceId = the element's train bar id). Setting one edits
  // the train's internal ties.
  const trainDepCandidates = React.useMemo<DepCandidate[]>(() => {
    if (!protoTrain) return [];
    return protoTrain.cars.map((c, i) => ({
      instanceId: String(TRAIN_ID_BASE - i),
      workCode: c.code,
      displayName: codeNameByKey[c.code] ?? c.code,
      taskName: codeNameByKey[c.code] ?? c.code,
      zoneLabel: "",
      startIso: trainBoardDates?.get(c.carId)?.start ?? null,
      finishIso: trainBoardDates?.get(c.carId)?.finish ?? null,
      color: effectiveCodeColors[c.code] ?? hashColor(c.code),
    }));
  }, [protoTrain, codeNameByKey, effectiveCodeColors, trainBoardDates]);

  const trainDrivingPredByBar = React.useMemo(() => {
    const m = new Map<
      number,
      {
        code: string;
        color: string;
        name: string;
        rel: string;
        predId: string;
        type: TrainDepType;
        lagDays: number;
      }
    >();
    if (!protoTrain) return m;
    const idxByCar = new Map(protoTrain.cars.map((c, i) => [c.carId, i]));
    protoTrain.cars.forEach((car, i) => {
      const dep = protoTrain.deps.find((d) => d.succCarId === car.carId);
      if (!dep) return;
      const predIdx = idxByCar.get(dep.predCarId);
      if (predIdx == null) return;
      const predCar = protoTrain.cars[predIdx];
      m.set(TRAIN_ID_BASE - i, {
        code: predCar.code,
        color: effectiveCodeColors[predCar.code] ?? hashColor(predCar.code),
        name: codeNameByKey[predCar.code] ?? predCar.code,
        rel: `${dep.type}${formatLagSuffix(dep.lagDays * 8)}`,
        predId: String(TRAIN_ID_BASE - predIdx),
        type: dep.type,
        lagDays: dep.lagDays,
      });
    });
    return m;
  }, [protoTrain, codeNameByKey, effectiveCodeColors]);

  const carIdOfTrainBar = React.useCallback(
    (barId: number): string | null =>
      protoTrain?.cars[TRAIN_ID_BASE - barId]?.carId ?? null,
    [protoTrain],
  );

  // Hover tooltip for a TRAIN-lane bar: its duration + what drives it (preds)
  // and what it drives (succs), since the synthetic train member carries none of
  // that. Returns undefined for normal board bars (they keep the default tip).
  const trainBarTip = React.useCallback(
    (m: ScopeMember): string | undefined => {
      if (!protoTrain) return undefined;
      const carId = carIdOfTrainBar(m.activity_id);
      if (!carId) return undefined;
      const car = protoTrain.cars.find((c) => c.carId === carId);
      if (!car) return undefined;
      const codeOf = (id: string) =>
        protoTrain.cars.find((c) => c.carId === id)?.code ?? "?";
      const name = codeNameByKey[car.code] ?? car.code;
      const preds = protoTrain.deps.filter((d) => d.succCarId === carId);
      const succs = protoTrain.deps.filter((d) => d.predCarId === carId);
      const parts = [`${name} « ${car.code} »`, `${car.durationDays}d`];
      parts.push(
        preds.length
          ? `after: ${preds
              .map(
                (d) =>
                  `${codeOf(d.predCarId)} ${d.type}${d.lagDays ? `+${d.lagDays}d` : ""}`,
              )
              .join(", ")}`
          : "after: — (start of run)",
      );
      if (succs.length) {
        parts.push(`before: ${succs.map((d) => codeOf(d.succCarId)).join(", ")}`);
      }
      return parts.join("  ·  ");
    },
    [protoTrain, carIdOfTrainBar, codeNameByKey],
  );
  const handleSetTrainPred = React.useCallback(
    (
      succBarId: number,
      oldPredId: string | null,
      newPredId: string | null,
      type: TrainDepType,
      lagDays: number,
    ) => {
      if (!protoTrain) return;
      const succCar = carIdOfTrainBar(succBarId);
      if (!succCar) return;
      const oldPredCar = oldPredId != null ? carIdOfTrainBar(Number(oldPredId)) : null;
      const newPredCar = newPredId != null ? carIdOfTrainBar(Number(newPredId)) : null;
      const { deps, cycle } = replaceTrainDep(
        protoTrain.deps, oldPredCar, succCar, newPredCar, succCar, type, lagDays,
      );
      if (cycle) {
        notify("Can't set this tie — it would create a loop.", "error");
        return;
      }
      pushUndo("Train tie");
      setProtoTrain((t) => (t ? { ...t, deps } : t));
    },
    [protoTrain, carIdOfTrainBar, pushUndo],
  );
  const handleRemoveTrainPred = React.useCallback(
    (succBarId: number, predId: string) => {
      const succCar = carIdOfTrainBar(succBarId);
      const predCar = carIdOfTrainBar(Number(predId));
      if (!succCar || !predCar) return;
      pushUndo("Remove train tie");
      setProtoTrain((t) =>
        t ? { ...t, deps: removeTrainDep(t.deps, predCar, succCar) } : t,
      );
    },
    [carIdOfTrainBar, pushUndo],
  );

  // The pinned virtual train lane: project protoTrain.cars into a synthetic
  // LeafRow — an EDITABLE template row. Static mode keeps each element at its
  // hand-set position (drag to arrange, no jumping); the Forward-pass toggle
  // simulates the schedule. Apply syncs the mapped instances.
  const trainRow = React.useMemo<LeafRow | null>(() => {
    if (!protoTrain) return null;
    // A blank train still renders an empty board row so codes can be dropped /
    // tap-added onto it (that's how the first car gets created from scratch).
    if (protoTrain.cars.length === 0) {
      return {
        key: TRAIN_LEAF_KEY,
        label: "🚂 Train",
        path: [TRAIN_LEAF_KEY],
        bars: [],
        undated: [],
        lanes: 1 + (protoExtraLanes.get(TRAIN_LEAF_KEY) ?? 0),
      };
    }
    const placed = protoTrain.cars.map((car, i) => {
      // The board's train row stays HAND-PLACED regardless of the gantt's
      // Forward-pass toggle — dragging a car never snaps back (matches the
      // section board, where the pass shows in the gantt, not on the board).
      const startIndex = Math.max(0, car.startIndex);
      return { car, i, startIndex, durationDays: car.durationDays };
    });
    // FS chains: a car that starts after another (Finish→Start) follows that
    // car's lane, so a run reads as one row — same as the board's fsLinks
    // grouping. SS/FF/untied cars greedy-fill around them.
    const fsPredOf = new Map<string, string>();
    for (const d of protoTrain.deps) {
      if (d.type === "FS") fsPredOf.set(d.succCarId, d.predCarId);
    }
    // Pack into stacked lanes so two cars never sit on top of each other, but
    // keep an FS successor on its predecessor's lane when it fits there.
    const laneEnd: number[] = [];
    const laneByCar = new Map<string, number>();
    for (const p of [...placed].sort(
      (a, b) => a.startIndex - b.startIndex || a.durationDays - b.durationDays,
    )) {
      let lane = -1;
      const predCarId = fsPredOf.get(p.car.carId);
      if (predCarId != null) {
        const predLane = laneByCar.get(predCarId);
        if (predLane != null && p.startIndex >= (laneEnd[predLane] ?? 0)) {
          lane = predLane;
        }
      }
      if (lane === -1) {
        lane = laneEnd.findIndex((end) => p.startIndex >= end);
        if (lane === -1) {
          lane = laneEnd.length;
          laneEnd.push(0);
        }
      }
      laneEnd[lane] = p.startIndex + p.durationDays;
      laneByCar.set(p.car.carId, lane);
    }
    const bars: Bar[] = placed.map(({ car, i, startIndex, durationDays }) => ({
      member: {
        activity_id: TRAIN_ID_BASE - i,
        task_code: car.code,
        task_name: codeNameByKey[car.code] ?? car.code,
        status: "TK_NotStart",
        restart_date: null,
        reend_date: null,
        actual_start: null,
        actual_finish: null,
        original_duration: car.durationDays,
        takt_section_name: "",
        takt_a: "",
        takt_b: "",
        takt_c: "",
        takt_work: car.code,
        wbs_node_id: -1,
        wbs_path: [TRAIN_LEAF_KEY],
      },
      startIndex,
      durationDays,
      lane: laneByCar.get(car.carId) ?? 0,
    }));
    return {
      key: TRAIN_LEAF_KEY,
      label: "🚂 Train",
      path: [TRAIN_LEAF_KEY],
      bars,
      undated: [],
      lanes:
        Math.max(1, laneEnd.length) +
        (protoExtraLanes.get(TRAIN_LEAF_KEY) ?? 0),
    };
  }, [protoTrain, codeNameByKey, protoExtraLanes]);

  // Undo a staged delete: the activity returns to the board fully, pinned to
  // its own empty row at the bottom of its level (not packed mid-sequence).

  // ---- LEVEL COPY: rewrite a target level from a source level ---------- //
  const kbdRef = React.useRef<{
    count: number;
    canUndo: boolean;
    blocked: boolean;
    clear: () => void;
    del: () => void;
    all: () => void;
    undo: () => void;
  }>({
    count: 0, canUndo: false, blocked: true,
    clear: () => {}, del: () => {}, all: () => {}, undo: () => {},
  });
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const a = kbdRef.current;
      if (a.blocked) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) {
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z") && !e.shiftKey) {
        if (a.canUndo) {
          e.preventDefault();
          a.undo();
        }
      } else if (e.key === "Escape") {
        if (a.count > 0) {
          e.preventDefault();
          a.clear();
        }
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (a.count > 0) {
          e.preventDefault();
          a.del();
        }
      } else if ((e.metaKey || e.ctrlKey) && (e.key === "a" || e.key === "A")) {
        e.preventDefault();
        a.all();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Keyboard wiring (assigned here, after everything it references exists).
  const anyModalOpen =
    addModalOpen || codeEditTarget != null || deleteImpact != null ||
    dropConflict != null || bulkDrop != null;
  kbdRef.current = {
    count: boardVisible ? protoSelection.size : 0,
    canUndo: undoCount > 0,
    blocked: readOnly || anyModalOpen,
    clear: () => setProtoSelection(new Set()),
    del: () => protoBulkDelete(),
    all: () =>
      setProtoSelection(
        new Set(
          protoMembers
            .filter((m) => m.status !== "TK_Complete")
            .map((m) => m.activity_id),
        ),
      ),
    undo,
  };

  // PROTOTYPE: string-keyed anchor map + WBS-leaf lookup for the deps view.
  const anchorTagsByGraphId = React.useMemo(() => {
    const m = new Map<string, AnchorDir>();
    anchorDirById.forEach((dir, aid) => m.set(String(aid), dir));
    return m;
  }, [anchorDirById]);

  const wbsLeafOf = React.useMemo(() => {
    const byId = new Map<string, { key: string; label: string; order: number }>();
    const paths = new Map<string, string[]>();
    for (const m of scopeMembers ?? []) {
      const path = m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"];
      paths.set(path.join(" "), path);
    }
    const ordered = Array.from(paths.keys()).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
    const orderByKey = new Map(ordered.map((k, i) => [k, i]));
    for (const m of scopeMembers ?? []) {
      const path = m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"];
      const key = path.join(" ");
      byId.set(String(m.activity_id), {
        key,
        label: path[path.length - 1],
        order: orderByKey.get(key) ?? Number.MAX_SAFE_INTEGER,
      });
    }
    for (const n of protoNewActs) {
      byId.set(n.draftId, {
        key: n.wbsKey,
        label: n.wbsPath[n.wbsPath.length - 1] ?? n.wbsKey,
        order: orderByKey.get(n.wbsKey) ?? Number.MAX_SAFE_INTEGER,
      });
    }
    return (id: string) => byId.get(id) ?? null;
  }, [scopeMembers, protoNewActs]);

  // Manual lane pins merged for the board (committed ids + new-bar entries).
  const protoLaneOverrides = React.useMemo(() => {
    const m = new Map(protoLanes);
    protoNewActs.forEach((n, i) => {
      if (!n.deleted && n.lane != null) m.set(-(i + 1), n.lane);
    });
    return m;
  }, [protoLanes, protoNewActs]);

  // Tap a strip chip: append a new activity to the selected lane, right after
  // its last bar, tied FS to it (no pred when the lane is empty). One undo.
  const handleTapCode = React.useCallback(
    (code: string) => {
      if (readOnly) return;
      const sel = selectedLane;
      if (!sel) return;
      const rows = computeLeafRows({
        members: protoMembers,
        calendarIndexFor: protoCalendarIndexFor,
        spanOverrides: protoSpans,
        laneOverrides: protoLaneOverrides,
        rowOrder: protoLeafOrder ?? undefined,
        extraRows: protoExtraRows.map((label) => ({ key: label, label })),
        extraLanes: protoExtraLanes,
        seedLeaves: allScopeLeaves,
        fsLinks: boardFsLinks,
      });
      // The train row participates in lane-tap exactly like any board row — the
      // append stages as a new car via stageProtoDrop's TRAIN_LEAF_KEY branch.
      const leaf =
        sel.leafKey === TRAIN_LEAF_KEY
          ? trainRow
          : rows.find((l) => l.key === sel.leafKey);
      // The armed lane may have been removed (or its row deleted/reordered)
      // since selection — appending to a phantom lane would land a bar at day 0
      // with no predecessor. Disarm instead.
      if (!leaf || sel.lane >= leaf.lanes) {
        setSelectedLane(null);
        return;
      }
      const laneBars = leaf.bars.filter((b) => b.lane === sel.lane);
      let last: (typeof laneBars)[number] | null = null;
      for (const b of laneBars) {
        if (!last || b.startIndex + b.durationDays > last.startIndex + last.durationDays) {
          last = b;
        }
      }
      const startIndex = last ? last.startIndex + last.durationDays : 0;
      const predRef =
        last == null
          ? undefined
          : sel.leafKey === TRAIN_LEAF_KEY
            ? // a train car's synthetic id; the train-drop branch maps it to a
              // carId and ties the new car FS to it (like the board's lane-tap).
              last.member.activity_id
            : last.member.activity_id < 0
              ? protoNewActs[-last.member.activity_id - 1]?.draftId
              : last.member.activity_id;
      stageProtoDrop({ key: leaf.key, path: leaf.path }, startIndex, code, {
        lane: sel.lane,
        predRef,
      });
    },
    // setSelectedLane setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      readOnly,
      trainRow,
      selectedLane,
      protoMembers,
      protoCalendarIndexFor,
      protoSpans,
      protoLaneOverrides,
      protoLeafOrder,
      protoExtraRows,
      protoExtraLanes,
      allScopeLeaves,
      protoNewActs,
      stageProtoDrop,
      boardFsLinks,
    ],
  );

  // Driving predecessor per BOARD bar id (recomputed live by the graph pass).
  const drivingPredByBarId = React.useMemo(() => {
    const m = new Map<
      number,
      {
        code: string;
        color: string;
        name: string;
        rel: string;
        predId: string;
        type: DepGraphPredType;
        lagDays: number;
      }
    >();
    if (!depGraph) return m;
    const negByDraft = new Map<string, number>();
    protoNewActs.forEach((n, i) => {
      if (!n.deleted) negByDraft.set(n.draftId, -(i + 1));
    });
    for (const [succGid, edges] of Object.entries(depGraph.edges)) {
      const e = edges.find((edge) => edge.driving);
      if (!e) continue;
      const pred = depGraph.activities[e.pred_id];
      if (!pred) continue;
      const barId = /^\d+$/.test(succGid)
        ? Number(succGid)
        : negByDraft.get(succGid);
      if (barId == null) continue;
      m.set(barId, {
        code: pred.work_code,
        color:
          pred.color ||
          effectiveCodeColors[pred.work_code] ||
          (pred.work_code ? hashColor(pred.work_code) : ""),
        name: activityLabel(pred),
        rel: `${e.type}${formatLagSuffix(e.lag_hours)}`,
        predId: e.pred_id,
        type: e.type,
        lagDays: e.lag_hours / 8,
      });
    }
    return m;
  }, [depGraph, protoNewActs, effectiveCodeColors]);

  // In-section candidates for the popover's "+ add" pred picker.
  const depCandidates = React.useMemo(
    () => (depGraph ? buildCandidates(Object.values(depGraph.activities), depGraph.link_candidates ?? []) : []),
    [depGraph],
  );

  // Set / replace / retype a bar's predecessor in ONE staged step (one undo).
  // Setting a tie SNAPS the bar to where that tie puts it (FS: after the
  // pred + lag; SS: pred start + lag; FF: finishes together + lag; SF: no
  // snap). Only here — later drags move it freely.
  const handleSetPredEdge = React.useCallback(
    (
      barId: number,
      currentPredId: string | null,
      newPredId: string | null,
      type: DepGraphPredType,
      lagDays: number,
    ) => {
      const g = depGraphRef.current;
      if (!g) return;
      const succ = barGraphId(barId);
      const snapToPred = (predGid: string) => {
        if (type === "SF") return;
        const predBarId = /^\d+$/.test(predGid)
          ? Number(predGid)
          : (() => {
              const i = protoNewActs.findIndex(
                (n) => !n.deleted && n.draftId === predGid,
              );
              return i >= 0 ? -(i + 1) : null;
            })();
        if (predBarId == null) return;
        const predSpan = protoSpanOf(predBarId);
        const succSpan = protoSpanOf(barId);
        if (!predSpan || !succSpan) return;
        let start: number;
        if (type === "FS") {
          start = predSpan.startIndex + predSpan.durationDays + lagDays;
        } else if (type === "SS") {
          start = predSpan.startIndex + lagDays;
        } else {
          // FF: finish lands lag after the pred's finish.
          start =
            predSpan.startIndex +
            predSpan.durationDays +
            lagDays -
            succSpan.durationDays;
        }
        start = Math.max(
          0,
          Math.min(Math.round(start), protoCalendar.length - succSpan.durationDays),
        );
        if (start === succSpan.startIndex) return;
        squelchUndoRef.current = true;
        try {
          handleProtoMove([{ activityId: barId, startIndex: start }]);
        } finally {
          squelchUndoRef.current = false;
        }
      };
      if (newPredId && newPredId !== currentPredId) {
        if (wouldCreateCycle(g, newPredId, succ)) {
          notify(
            "Can't add this tie — it would create a dependency loop (the predecessor already depends on this activity).",
            "error",
          );
          return;
        }
        pushUndo("Pred edit");
        setPendingOps((prev) => {
          let edits = prev.pending_dep_edits;
          if (currentPredId) edits = removeEdge(edits, g, succ, currentPredId);
          edits = addEdge(edits, g, newPredId, succ, type, lagDays);
          return { ...prev, pending_dep_edits: edits };
        });
        snapToPred(newPredId);
        return;
      }
      if (currentPredId) {
        handleRetypeEdge(succ, currentPredId, type, lagDays);
        snapToPred(currentPredId);
      }
    },
    // depGraphRef is a stable ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      barGraphId,
      pushUndo,
      handleRetypeEdge,
      protoNewActs,
      protoSpanOf,
      protoCalendar.length,
      handleProtoMove,
    ],
  );

  const handleRemovePredEdge = React.useCallback(
    (barId: number, predId: string) => {
      const g = depGraphRef.current;
      if (!g) return;
      const succ = barGraphId(barId);
      pushUndo("Remove predecessor");
      setPendingOps((prev) => ({
        ...prev,
        pending_dep_edits: removeEdge(prev.pending_dep_edits, g, succ, predId),
      }));
    },
    // depGraphRef is a stable ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [barGraphId, pushUndo],
  );

  // Row facts for the lane's column list: CURRENT sandbox values + the
  // ORIGINAL P6 values wherever a staged change diverges (rename, recode,
  // duration, dates).
  const laneRowFacts = React.useMemo(() => {
    const originals = new Map((scopeMembers ?? []).map((m) => [m.activity_id, m]));
    const out = new Map<
      number,
      {
        code: string;
        codeColor: string;
        days: number;
        start: string | null;
        finish: string | null;
        isNew: boolean;
        deleted?: boolean;
        was: { name?: string; code?: string; days?: number; start?: string; finish?: string };
      }
    >();
    const deletedIds = new Set(
      pendingOps.pending_activity_deletes.map((d) => d.activity_id),
    );
    for (const m of scopeMembers ?? []) {
      if (!deletedIds.has(m.activity_id)) continue;
      out.set(m.activity_id, {
        code: m.takt_work,
        codeColor: m.takt_work
          ? effectiveCodeColors[m.takt_work] ?? hashColor(m.takt_work)
          : "",
        days: Math.max(1, Math.round(m.original_duration ?? 1)),
        start: m.restart_date,
        finish: m.reend_date,
        isNew: false,
        deleted: true,
        was: {},
      });
    }
    for (const m of protoMembers) {
      const span = protoSpanOf(m.activity_id);
      const start = span && span.startIndex >= 0 ? protoCalendar[span.startIndex] ?? null : m.restart_date;
      const finish =
        span && span.startIndex >= 0
          ? protoCalendar[
              Math.min(span.startIndex + span.durationDays - 1, protoCalendar.length - 1)
            ] ?? null
          : m.reend_date;
      const days = span?.durationDays ?? Math.max(1, Math.round(m.original_duration ?? 1));
      const was: {
        name?: string;
        code?: string;
        days?: number;
        start?: string;
        finish?: string;
      } = {};
      const orig = m.activity_id > 0 ? originals.get(m.activity_id) : undefined;
      if (orig?.status === "TK_Complete") {
        // Completed work is locked history: show its actual dates, never a
        // replanned span or a "changed" flag (restart/reend are NULLed by
        // mark_complete). It also never renders a bar on the board.
        out.set(m.activity_id, {
          code: m.takt_work ? codeDisplay[m.takt_work] ?? m.takt_work : "",
          codeColor: m.takt_work
            ? effectiveCodeColors[m.takt_work] ?? hashColor(m.takt_work)
            : "",
          days,
          start: orig.actual_start,
          finish: orig.actual_finish,
          isNew: false,
          was: {},
        });
        continue;
      }
      if (orig) {
        if (orig.task_name !== m.task_name) was.name = orig.task_name;
        if ((orig.takt_work || "") !== (m.takt_work || "")) was.code = orig.takt_work || "—";
        // Compare the committed span on the SAME basis as `days` (the bar's
        // calendar-day span) — not the rounded workday `original_duration`, or a
        // sub-day / weekend-spanning activity reads as a phantom resize on every
        // open (the board bar never moved).
        const origDays = memberSpan(orig, protoCalendarIndexFor).durationDays;
        if (origDays !== days) was.days = origDays;
        const d10 = (s: string | null) => (s ? s.slice(0, 10) : null);
        if (d10(orig.restart_date) !== d10(start)) was.start = d10(orig.restart_date) ?? "—";
        if (d10(orig.reend_date) !== d10(finish)) was.finish = d10(orig.reend_date) ?? "—";
      }
      out.set(m.activity_id, {
        code: m.takt_work ? codeDisplay[m.takt_work] ?? m.takt_work : "",
        codeColor: m.takt_work
          ? effectiveCodeColors[m.takt_work] ?? hashColor(m.takt_work)
          : "",
        days,
        start,
        finish,
        isNew: m.activity_id < 0,
        was,
      });
    }
    return out;
  }, [scopeMembers, protoMembers, protoSpanOf, protoCalendar, protoCalendarIndexFor, effectiveCodeColors, codeDisplay, pendingOps.pending_activity_deletes]);

  // PROTOTYPE: the lane list shows EVERY scope member (planned included).
  const allLaneMembers = React.useMemo<LaneMember[]>(() => {
    if (!scopeMembers) return [];
    const out: LaneMember[] = [];
    const deletedIds = new Set(
      pendingOps.pending_activity_deletes.map((d) => d.activity_id),
    );
    for (const m of scopeMembers) {
      if (!deletedIds.has(m.activity_id)) continue;
      const span = memberSpan(m, protoCalendarIndexFor);
      out.push({
        member: m,
        state: "completed", // display-only: not selectable, not draggable
        claimed: false,
        startIndex: span.startIndex,
        durationDays: span.durationDays,
      });
    }
    for (const m of protoMembers) {
      const st = memberStateById.get(m.activity_id);
      // CURRENT sandbox span (drags/merges included), so the list orders and
      // positions by what the board shows.
      const span =
        protoSpans.get(m.activity_id) ?? memberSpan(m, protoCalendarIndexFor);
      out.push({
        member: m,
        state: st == null || st === "planned" ? "unresolved" : st,
        claimed: sectionClaims.has(m.activity_id),
        startIndex: span.startIndex,
        durationDays: span.durationDays,
      });
    }
    out.sort(
      (a, b) =>
        a.startIndex - b.startIndex ||
        a.member.task_code.localeCompare(b.member.task_code),
    );
    return out;
  }, [scopeMembers, protoMembers, memberStateById, sectionClaims, protoCalendarIndexFor, protoSpans, pendingOps.pending_activity_deletes]);

  // True when the plan window straddles a year boundary — the ledger then
  // stamps dates with a year so 12/30 vs 1/2 aren't ambiguous.
  const ledgerShowYear =
    protoCalendar.length > 0 &&
    protoCalendar[0].slice(0, 4) !==
      protoCalendar[protoCalendar.length - 1].slice(0, 4);

  React.useEffect(() => {
    onLedgerSnapshotChange?.({
      members: allLaneMembers,
      rowFacts: laneRowFacts,
      anchorDir: anchorDirById,
      showYear: ledgerShowYear,
    });
  }, [onLedgerSnapshotChange, allLaneMembers, laneRowFacts, anchorDirById, ledgerShowYear]);

  // The board selection drives train capture — its count + actions live in the
  // train band's no-train header now (Capture as train / Delete / Clear).
  const selCount = protoSelection.size;

  const savePill =
    !readOnly && draftId != null && saveState !== "idle" ? (
      <span className={`pull-plan-save-dot pull-plan-save-dot--${saveState}`}>
        <span className="pull-plan-save-dot-mark" aria-hidden="true" />
        {saveState === "saving" && "Saving…"}
        {saveState === "saved" && "Saved"}
        {saveState === "error" && (
          <>
            Not saved
            <button
              type="button"
              className="pull-plan-save-retry"
              onClick={retrySave}
            >
              Retry
            </button>
          </>
        )}
      </span>
    ) : null;

  const targetChip =
    pullTarget && pullTarget.mode !== "skip" ? (
      <span
        className="pull-plan-target-chip"
        title={
          pullTarget.mode === "milestone"
            ? `Pull target: ${pullTarget.label}`
            : "Pull target: custom dates per level"
        }
      >
        ◎{" "}
        {pullTarget.mode === "milestone"
          ? `${pullTarget.work_code ? `\u00ab${pullTarget.work_code}\u00bb ` : ""}${pullTarget.label}`
          : "custom dates"}
      </span>
    ) : null;

  // The gantt's single header content — the title / focus chip / actions that
  // share ONE row with Board-dates / Forward-pass / External-ties (SequenceView
  // renders them in its mode toolbar via headerLeft).
  const focusedLeafLabel = focusedLeaf
    ? allScopeLeaves.find((l) => l.key === focusedLeaf)?.label ?? focusedLeaf
    : null;
  const focusedLeafLvl = focusedLeaf
    ? effectiveLevelByLeaf.get(focusedLeaf) ?? null
    : null;
  const sectionGanttHeaderLeft = (
    <div className="pull-plan-logic-row">
      <span className="pull-plan-logic-label">Logic</span>
      <span className="pull-plan-logic-level">
        {focusedLeaf ? focusedLeafLabel : "All levels"}
      </span>
      {focusedLeafLvl && (
        <span className="pull-plan-logic-tag">{focusedLeafLvl}</span>
      )}
    </div>
  );
  const trainGanttHeaderLeft = (
    <div className="pull-plan-logic-col">
      <div className="pull-plan-logic-row">
        <span className="pull-plan-logic-label">Logic</span>
        <span className="pull-plan-logic-level">Train</span>
        <span className="pull-plan-logic-tag">TRAIN</span>
      </div>
      <span className="unified-board-gantt-sub">
        level-agnostic · relative days · ties apply to every level
      </span>
    </div>
  );

  // The gantt pane's content: the train's own ties when focused on the train,
  // otherwise the section's dependency graph.
  const trainGanttPane =
    trainGraph != null ? (
      <SequenceView
        key="train-ties"
        flat
        headerLeft={trainGanttHeaderLeft}
        variant="plan"
        mode={trainSeqMode}
        onModeChange={setTrainSeqMode}
        modeNote="Forward pass recalculates dates from your ties"
        onApplyDates={readOnly ? undefined : applyDatesToTrain}
        applyDatesLabel="⇩ Apply dates to train"
        applyDatesNote="Applies the Predecessors driven dates to the train"
        graph={trainGraph}
        loading={false}
        error={null}
        readOnly={readOnly}
        intents={trainSeqIntents}
        boardDates={trainBoardDates}
        onEditDuration={handleUpdateTrainCarDuration}
        onApplyEdit={(id, args) => handleUpdateTrainCarDuration(id, args.days)}
        onRequestDelete={handleRemoveTrainCars}
      />
    ) : null;

  const sectionGanttPane = depEditorSection ? (
    <SequenceView
      key="section-seq"
      headerLeft={sectionGanttHeaderLeft}
      variant="plan"
      fullscreen={logicFullscreen}
      onToggleFullscreen={toggleLogicFullscreen}
      mode={seqMode}
      onModeChange={setSeqMode}
      modeNote="Forward pass recalculates dates from your ties"
      onApplyDates={readOnly ? undefined : applyGraphDatesToBoard}
      applyDatesLabel="⇩ Apply dates to board"
      applyDatesNote="Applies the Predecessors driven dates to the board"
      applyDatesActive={applyDatesDirty}
      onShowAll={() => setFocusedLeaf(null)}
      boardDates={boardDatesByGid}
      boardZones={boardZones}
      wbsLeafOf={wbsLeafOf}
      focusLeaf={focusedLeaf}
      anchorTags={anchorTagsByGraphId}
      graph={depGraph}
      loading={depGraphLoading}
      error={depGraphError}
      readOnly={readOnly}
      intents={{
        onAddEdge: handleAddEdge,
        onRemoveEdge: handleRemoveEdge,
        onRetypeEdge: handleRetypeEdge,
        onReplaceEdge: handleReplaceEdge,
      }}
      fetchExternals={fetchExternals}
      onEditDuration={handleSeqEditDuration}
      onApplyEdit={handleApplyEdit}
      onRequestDelete={(ids) => void requestDelete(ids)}
      inlineAdd={inlineAddConfig}
      localAdds={enrichedLocalAdds}
      onEditingHoldChange={inlineAdd.onHoldChange}
      reviewedAnchors={reviewedAnchors}
      onToggleAnchorReview={toggleAnchorReview}
    />
  ) : null;

  // The gantt follows the SELECTED row: the train band's row → the train's ties
  // (level-agnostic); a board level → that level's section ties. Never locked —
  // click any row to inspect its ties.
  const ganttPane = ganttVisible
    ? trainTiesView && protoTrain
      ? trainGanttPane
      : sectionGanttPane
    : null;

  // Add / remove an empty lane on any row (board level OR the train) — shared
  // so the train participates in lane management exactly like a board row.
  const handleChangeLanes = React.useCallback(
    (leafKey: string, delta: number) => {
      pushUndo(delta > 0 ? "Add lane" : "Remove lane");
      if (delta < 0) {
        setSelectedLane((sel) => (sel && sel.leafKey === leafKey ? null : sel));
      }
      setProtoExtraLanes((prev) => {
        const next = new Map(prev);
        const n = Math.max(0, (next.get(leafKey) ?? 0) + delta);
        if (n === 0) next.delete(leafKey);
        else next.set(leafKey, n);
        return next;
      });
    },
    [pushUndo, setSelectedLane, setProtoExtraLanes],
  );
  // Selecting the train band's row focuses the gantt on the train's ties.
  const handleSelectTrainTies = React.useCallback(() => {
    setTrainTiesView(true);
    setFocusedLeaf(null);
    setPaneMode((m) => (m === "board" ? "both" : m));
  }, [setTrainTiesView, setFocusedLeaf, setPaneMode]);

  // ---- Train band (lifted out of the board, above it) ------------------
  // The band's track is the REAL train rendered through WbsBoardView — same
  // drag / drop / delete / lane-tap / lane-select machinery as the board, so
  // nothing here duplicates train logic. A train-width slice of the calendar
  // keeps the band tight.
  const trainTrackSpan = trainRow
    ? Math.max(6, ...trainRow.bars.map((b) => b.startIndex + b.durationDays)) + 4
    : 12;
  const trainBandCalendar = React.useMemo(
    () => protoCalendar.slice(0, Math.min(protoCalendar.length, trainTrackSpan)),
    [protoCalendar, trainTrackSpan],
  );
  // Selected train cars (marquee / ctrl-click within the train track).
  const trainSelCount = React.useMemo(
    () => Array.from(protoSelection).filter(isTrainCarId).length,
    [protoSelection],
  );
  // Bulk-drag selected cars: shift each car's static position; moveBulk skips
  // train cars (no protoSpanOf), so route them straight through handleProtoMove,
  // which sets their position via setTrainCarPosition.
  const handleTrainBulkDrop = React.useCallback(
    (intent: { ids: number[]; dxDays: number; leafKey: string | null; lane: number | null }) => {
      if (!protoTrain || intent.dxDays === 0) return;
      const moves = intent.ids
        .filter(isTrainCarId)
        .map((id) => {
          const car = protoTrain.cars[TRAIN_ID_BASE - id];
          return car
            ? { activityId: id, startIndex: Math.max(0, car.startIndex + intent.dxDays) }
            : null;
        })
        .filter((m): m is { activityId: number; startIndex: number } => m != null);
      if (moves.length > 0) handleProtoMove(moves);
    },
    [protoTrain, handleProtoMove],
  );
  // "Select from here onward": from the earliest-positioned selected car, select
  // it and every car at or after its position in the sequence.
  const handleSelectTrainFromHere = React.useCallback(() => {
    if (!trainRow) return;
    setProtoSelection(
      fromHereSelection(
        trainRow.bars.map((b) => ({ id: b.member.activity_id, startIndex: b.startIndex })),
        protoSelection,
      ),
    );
  }, [trainRow, protoSelection, setProtoSelection]);
  const trainTrackSlot =
    protoTrain && trainRow ? (
      <div
        className="train-band-board"
        style={{ width: protoLabelWidth + trainBandCalendar.length * DAY_PX }}
      >
        <DayHeader calendar={trainBandCalendar} labelWidth={protoLabelWidth} relative />
        <WbsBoardView
          members={[]}
          trainRow={trainRow}
          calendar={trainBandCalendar}
          calendarIndexFor={protoCalendarIndexFor}
          workCodeColors={effectiveCodeColors}
          anchorDir={anchorDirById}
          dayPx={DAY_PX}
          labelWidth={protoLabelWidth}
          laneHeight={LANE_HEIGHT}
          readOnly={readOnly}
          selectedLane={selectedLane}
          onSelectLane={setSelectedLane}
          selectedLeafKey={trainTiesView ? TRAIN_LEAF_KEY : null}
          onSelectLeaf={handleSelectTrainTies}
          extraLanes={protoExtraLanes}
          onChangeLanes={handleChangeLanes}
          dropArmed={!readOnly && draggingActivityKey != null}
          onDropCode={handleProtoDrop}
          onRemoveNew={handleProtoRemoveNew}
          spanOverrides={protoSpans}
          laneOverrides={protoLaneOverrides}
          codeDisplay={codeDisplay}
          selectedIds={protoSelection}
          onSelectionChange={readOnly ? undefined : handleSelectionChange}
          onBulkDrop={readOnly ? undefined : handleTrainBulkDrop}
          onMoveBars={handleProtoMove}
          onResizeBar={handleProtoResize}
          onDeleteBar={handleProtoDelete}
          onRenameBar={handleProtoRename}
          onRecodeBar={handleProtoRecode}
          trainDepCandidates={trainDepCandidates}
          trainDrivingPredByBar={trainDrivingPredByBar}
          onSetTrainPred={readOnly ? undefined : handleSetTrainPred}
          onRemoveTrainPred={readOnly ? undefined : handleRemoveTrainPred}
          barGraphId={barGraphId}
          scheduleId={scheduleId}
          paletteCodes={paletteAsExistingCodes}
        />
      </div>
    ) : null;
  const handleNewBlankTrain = React.useCallback(() => {
    pushUndo("New blank train");
    setProtoTrain(emptyTrain());
    setProtoSelection(new Set());
    setFocusedLeaf(null);
    setTrainTiesView(true);
    // Arm the train's first lane so tapping a work code immediately builds the
    // run (adds an FS-tied car), per the fresh-train flow.
    setSelectedLane({ leafKey: TRAIN_LEAF_KEY, lane: 0 });
  }, [
    pushUndo,
    setProtoTrain,
    setProtoSelection,
    setFocusedLeaf,
    setTrainTiesView,
    setSelectedLane,
  ]);
  // Editable identity of the train (cars + ties + level count) — NOT its baselines
  // or instance refs, which Apply itself rewrites. So this is stable across an
  // Apply but changes on any user edit, driving the Apply button's ✓ done state.
  const trainSig = React.useMemo(() => {
    if (!protoTrain) return null;
    return JSON.stringify({
      c: protoTrain.cars.map((c) => [c.code, c.durationDays]),
      d: protoTrain.deps.map((x) => [x.predCarId, x.succCarId, x.type, x.lagDays]),
      n: protoTrain.instances.length,
    });
  }, [protoTrain]);
  const trainSigRef = React.useRef(trainSig);
  trainSigRef.current = trainSig;
  const trainApplied = trainAppliedSig != null && trainAppliedSig === trainSig;
  const handleApplyAndMark = React.useCallback(async () => {
    if (await handleApplyTrain()) setTrainAppliedSig(trainSigRef.current);
  }, [handleApplyTrain]);

  const trainCarCount = protoTrain?.cars.length ?? 0;
  const trainLevelCount = trainLevelChips.length;
  const trainInstanceCount = protoTrain?.instances.length ?? 0;
  const subtitle = `${allScopeLeaves.length} ${lvlWord(allScopeLeaves.length)} · ${protoMembers.length} ${actWord(protoMembers.length)}`;
  const trainKind = protoTrain?.kind ?? "captured";
  const isFreshTrain = protoTrain?.kind === "fresh";

  // The level (label) most of the current selection sits on — shown as the
  // source in the no-train band's "{N} activities selected · {Level}" line.
  const selSrcLabel = React.useMemo(() => {
    if (protoSelection.size === 0) return null;
    const counts = new Map<string, number>();
    let best: string | null = null;
    let bestN = 0;
    protoSelection.forEach((id) => {
      const key = memberIndex.leaf.get(id);
      if (!key) return;
      const n = (counts.get(key) ?? 0) + 1;
      counts.set(key, n);
      if (n > bestN) {
        bestN = n;
        best = key;
      }
    });
    return best ? allScopeLeaves.find((l) => l.key === best)?.label ?? null : null;
  }, [protoSelection, memberIndex, allScopeLeaves]);

  // FRESH train: each placement is an instance; every chip is removable (no
  // capture "source" level), and removing one tombstones that run's draft work.
  const freshPlacedChips = React.useMemo(
    () => trainLevelChips.map((c) => ({ ...c, isSource: false })),
    [trainLevelChips],
  );
  const handleTogglePlace = React.useCallback(() => {
    if (trainCarCount === 0) return;
    setPlaceArmed((a) => !a);
  }, [trainCarCount]);
  const handleDiscardFresh = React.useCallback(() => {
    setPlaceArmed(false);
    handleClearTrain();
  }, [handleClearTrain]);
  const handleRemovePlacement = React.useCallback(
    (index: number) => {
      if (!protoTrain) return;
      const inst = protoTrain.instances[index];
      if (inst) {
        pushUndo("Remove placement");
        deleteTrainMappedRefs(Object.values(inst));
        setProtoTrain((t) =>
          t ? { ...t, instances: t.instances.filter((_, i) => i !== index) } : t,
        );
      }
    },
    [protoTrain, deleteTrainMappedRefs, pushUndo, setProtoTrain],
  );
  // Stamp the fresh train's run onto a level as new draft work, preserving the
  // run's SHAPE: each car becomes a new activity at the clicked day + the car's
  // own day/lane offset, with the car's duration and FS ties. Anchored at the
  // clicked lane; existing work is never overwritten (the board pushes the run
  // down to free lanes). One placement = one removable "Placed on" instance.
  const placeTrainRun = React.useCallback(
    (leaf: { key: string; path: string[] }, clickDay: number, clickLane: number) => {
      if (!protoTrain || protoTrain.cars.length === 0) return;
      // Per-car lane from the live train-track layout (carId → lane).
      const carLane = new Map<string, number>();
      for (const b of trainRow?.bars ?? []) {
        const car = protoTrain.cars[TRAIN_ID_BASE - b.member.activity_id];
        if (car) carLane.set(car.carId, b.lane);
      }
      const minStart = Math.min(
        ...protoTrain.cars.map((c) => Math.max(0, c.startIndex)),
      );
      const minLane =
        carLane.size > 0 ? Math.min(...Array.from(carLane.values())) : 0;
      // Deterministic staging order (predecessors first); ties are wired after
      // every car exists, so a car's relationship to a later car still carries.
      const carById = new Map(protoTrain.cars.map((c) => [c.carId, c]));
      const order = fsPlacementOrder(protoTrain.cars, protoTrain.deps);
      // One placement = one undo step: snapshot once, then suppress the per-car
      // stageProtoDrop pushes so a single undo reverts the whole run + instance.
      pushUndo("Place train");
      squelchUndoRef.current = true;
      try {
        const draftByCar = new Map<string, string>();
        for (const carId of order) {
          const car = carById.get(carId);
          if (!car) continue;
          const relDay = Math.max(0, car.startIndex) - minStart;
          const relLane = (carLane.get(car.carId) ?? 0) - minLane;
          const id = stageProtoDrop(leaf, clickDay + relDay, car.code, {
            lane: Math.max(0, clickLane + relLane),
            duration: car.durationDays,
          });
          if (id) draftByCar.set(car.carId, id);
        }
        // Carry EVERY internal tie (all relationship types + every predecessor of
        // a car) once all cars exist — a single FS predRef per car dropped SS/FF/SF
        // and second-lane / merge ties.
        const tieEdits = placementTieEdits(protoTrain.deps, draftByCar);
        if (tieEdits.length > 0) {
          setPendingOps((prev) => ({
            ...prev,
            pending_dep_edits: [...prev.pending_dep_edits, ...tieEdits],
          }));
        }
        if (draftByCar.size > 0) {
          const instance: Record<string, string> = {};
          draftByCar.forEach((id, carId) => {
            instance[carId] = id;
          });
          setProtoTrain((t) =>
            t ? { ...t, instances: [...t.instances, instance] } : t,
          );
        }
      } finally {
        squelchUndoRef.current = false;
      }
    },
    // pushUndo / squelchUndoRef / setProtoTrain / setPendingOps are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoTrain, trainRow, stageProtoDrop, setProtoTrain, setPendingOps, pushUndo],
  );

  const boardHintNode: React.ReactNode = isFreshTrain ? (
    placeArmed ? (
      "Click a level to place the train · drag a placed run to nudge it"
    ) : (
      "tap a work code to add it to the train · then Select to place"
    )
  ) : (
    <>
      <b>tap</b> = add to selected lane (FS) · <b>drag</b> = place anywhere, tie
      manually
    </>
  );

  return (
    <div className="pull-plan-grid pull-plan-grid--full">
      {/* ===== TOP BAR ===== */}
      <div className="pull-plan-topbar">
        {headerLeft}
        <span className="pull-plan-topbar-div" aria-hidden="true" />
        {draftId != null && !readOnly ? (
          <PlanTitleMenu
            name={context.section.name}
            counts={subtitle}
            onDiscard={onDiscardDraft ?? (() => {})}
          />
        ) : (
          <>
            <h2>{context.section.name}</h2>
            <span className="pull-plan-topbar-subtitle">{subtitle}</span>
          </>
        )}

        <span className="pull-plan-grid-header-spacer" />
        {depEditorSection && view !== "anchors" && (
          <PaneModeToggle paneMode={paneMode} onChange={setPaneMode} />
        )}
        <span className="pull-plan-grid-header-spacer" />

        {depEditorSection && anchorsHasChange && (
          <button
            type="button"
            className={
              "pull-plan-topbar-anchors" +
              (view === "anchors" ? " pull-plan-topbar-anchors--active" : "")
            }
            title="Anchors: outside ties this plan severed (a deleted activity or a cut tie). Review each one so the outside work gets re-homed."
            onClick={() => goTab(view === "anchors" ? "board" : "anchors")}
          >
            Anchors
            {anchorStats.severed.length > 0 && (
              <span
                className="pull-plan-anchor-badge"
                title={`${anchorStats.severed.length} outside ${anchorStats.severed.length === 1 ? "tie" : "ties"} cut by this plan`}
              >
                {anchorStats.severed.length}
              </span>
            )}
          </button>
        )}
        {targetChip}
        {savePill}
        {/* Selection actions only surface with an active selection and no train
            open (a live train has its own selection bar), and never over the
            Anchors view where the board the selection came from isn't shown. */}
        {!readOnly && view !== "anchors" && protoTrain == null && selCount > 0 && (
          <div className="pull-plan-topbar-selacts">
            <span className="pull-plan-topbar-selcount">
              <b>{selCount}</b> selected{selSrcLabel ? ` · ${selSrcLabel}` : ""}
            </span>
            <button
              type="button"
              className="pull-plan-topbar-selbtn pull-plan-topbar-selbtn--del"
              title="Delete the selected activities"
              onClick={protoBulkDelete}
            >
              Delete
            </button>
            <button
              type="button"
              className="pull-plan-topbar-selbtn"
              title="Clear the selection"
              onClick={() => setProtoSelection(new Set())}
            >
              Clear
            </button>
          </div>
        )}
        {!readOnly && (
          <HistoryPanel items={historyItems} onUndo={undo} onJumpTo={jumpToHistory} />
        )}
        {depEditorSection && view !== "anchors" && !readOnly && protoTrain == null && (
          <CreateTrainMenu
            selCount={selCount}
            onFromSelection={handleCreateTrain}
            onNewEmpty={handleNewBlankTrain}
          />
        )}
        {!readOnly && <span className="pull-plan-topbar-div" aria-hidden="true" />}
        {headerRight}
      </div>

      {/* ===== TRAIN BAND (only while a train is open; selection / create-train
              live in the top bar). Hidden when the logic editor is dragged
              full-page over it. ===== */}
      {depEditorSection && view !== "anchors" && !readOnly && protoTrain != null && !logicCovered && (
        <TrainBand
          trainKind={trainKind}
          readOnly={readOnly}
          title={isFreshTrain ? "New train" : "Train"}
          meta={
            isFreshTrain
              ? `${trainCarCount} ${actWord(trainCarCount)} · ${trainInstanceCount > 0 ? `placed on ${trainInstanceCount} ${lvlWord(trainInstanceCount)}` : "no levels yet"}`
              : `${trainCarCount} ${actWord(trainCarCount)} · on ${trainLevelCount} ${lvlWord(trainLevelCount)}`
          }
          landLabel="Updates on"
          levelChips={isFreshTrain ? freshPlacedChips : trainLevelChips}
          onRemoveLevel={isFreshTrain ? handleRemovePlacement : handleRemoveTrainLevel}
          canAddLevel={trainCanAddLevel}
          onAddLevel={handleAddTrainLevel}
          onCancel={handleClearTrain}
          onApply={() => void handleApplyAndMark()}
          applyLabel={
            trainLevelCount > 0
              ? `Apply to ${trainLevelCount} ${lvlWord(trainLevelCount)}`
              : "Apply"
          }
          applyDisabled={trainInstanceCount === 0 || trainCarCount === 0}
          applied={trainApplied}
          consequence={
            trainCarCount === 0
              ? "Add work to the train to enable publishing."
              : `Updates ${trainCarCount} ${actWord(trainCarCount)} on ${trainLevelCount} ${lvlWord(trainLevelCount)} · everything else untouched.`
          }
          trackSlot={trainTrackSlot}
          placeArmed={placeArmed}
          canPlace={trainCarCount > 0}
          onTogglePlace={handleTogglePlace}
          onDiscard={handleDiscardFresh}
          selCount={selCount}
          onDelete={protoBulkDelete}
          onClear={() => setProtoSelection(new Set())}
          trainSelCount={trainSelCount}
          onSelectFromHere={handleSelectTrainFromHere}
        />
      )}
      {view === "anchors" && anchorsHasChange && depEditorSection && (
        <AnchorsBoard
          graph={depGraph}
          loading={depGraphLoading}
          error={depGraphError}
          readOnly={readOnly}
          intents={{
            onAddEdge: handleAddEdge,
            onRemoveEdge: handleRemoveEdge,
            onRetypeEdge: handleRetypeEdge,
            onReplaceEdge: handleReplaceEdge,
          }}
          reviewedAnchors={reviewedAnchors}
          onToggleAnchorReview={toggleAnchorReview}
          onBack={() => goTab("board")}
        />
      )}

      {(boardVisible || ganttVisible) && depEditorSection && (
      <UnifiedBoard
        paneMode={paneMode}
        covered={logicCovered}
        onCoverChange={setLogicCovered}
        fullscreen={logicFullscreen}
        ganttSlot={ganttPane}
        boardSlot={boardVisible ? (
      <div className="pull-plan-board-section">
        <ActivityPalette
          palette={visiblePalette}
          onDragStart={setDraggingActivityKey}
          onDragEnd={() => setDraggingActivityKey(null)}
          onOpenAdd={() => setAddModalOpen(true)}
          onEditCode={readOnly ? undefined : setCodeEditTarget}
          disableDrag={readOnly}
          onTapCode={readOnly ? undefined : handleTapCode}
          tapArmed={selectedLane != null}
          hint={boardHintNode}
          addCodePopover={
            addModalOpen ? (
              <AddWorkCodePopover
                scheduleId={scheduleId}
                extraCodes={paletteAsExistingCodes}
                onAdd={handleAddCodeToPalette}
                onClose={() => setAddModalOpen(false)}
              />
            ) : null
          }
        />
      <div className="pull-plan-grid-body-wrap">
        <div className="pull-plan-grid-main">
          <BulkDropFlow
            bulkDrop={bulkDrop}
            onMove={applyBulkMove}
            onDuplicate={applyBulkDuplicate}
            onClose={() => setBulkDrop(null)}
          />
          <DropConflictFlow
            dropConflict={dropConflict}
            palette={palette}
            effectiveCodeColors={effectiveCodeColors}
            protoCalendar={protoCalendar}
            protoSpanOf={protoSpanOf}
            handleProtoMove={handleProtoMove}
            stageProtoDrop={stageProtoDrop}
            onClose={() => setDropConflict(null)}
          />
        <div
          className={
            `pull-plan-grid-scroll` +
            (readOnly ? "" : " pull-plan-grid-scroll--plan-mode")
          }
          ref={gridScrollRef}
        >
          <div
            className="pull-plan-grid-body"
            style={{ width: protoGridWidth }}
            ref={gridBodyRef}
          >
            <DayHeader calendar={protoCalendar} labelWidth={protoLabelWidth} />

            {/* PROTOTYPE: rows are WBS leaves; every scope-leaf activity shows
                here — takted bars wear their takt pill, untakted bars are grey
                name pills, anchors are tagged ⚓ IN / ⚓ OUT. */}
            <WbsBoardView
              members={protoMembers}
              seedLeaves={allScopeLeaves}
              targetByLeaf={targetByLeaf}
              selectedLane={selectedLane}
              onSelectLane={setSelectedLane}
              selectedLeafKey={trainTiesView ? null : focusedLeaf}
              onSelectLeaf={(key) => {
                setTrainTiesView(false);
                setFocusedLeaf(key);
              }}
              depCandidates={depCandidates}
              drivingPredByBarId={drivingPredByBarId}
              onSetPredEdge={readOnly ? undefined : handleSetPredEdge}
              onRemovePredEdge={readOnly ? undefined : handleRemovePredEdge}
              trainDepCandidates={trainDepCandidates}
              trainDrivingPredByBar={trainDrivingPredByBar}
              onSetTrainPred={readOnly ? undefined : handleSetTrainPred}
              onRemoveTrainPred={readOnly ? undefined : handleRemoveTrainPred}
              barGraphId={barGraphId}
              calendar={protoCalendar}
              calendarIndexFor={protoCalendarIndexFor}
              workCodeColors={effectiveCodeColors}
              anchorDir={anchorDirById}
              dayPx={DAY_PX}
              labelWidth={protoLabelWidth}
              laneHeight={LANE_HEIGHT}
              dropArmed={!readOnly && draggingActivityKey != null}
              onDropCode={handleProtoDrop}
              placeArmed={placeArmed}
              onPlaceClick={readOnly ? undefined : placeTrainRun}
              onRemoveNew={handleProtoRemoveNew}
              spanOverrides={protoSpans}
              laneOverrides={protoLaneOverrides}
              fsLinks={boardFsLinks}
              codeDisplay={codeDisplay}
              onMoveBars={handleProtoMove}
              onBulkDrop={readOnly ? undefined : handleBulkDrop}
              onResizeBar={handleProtoResize}
              onDeleteBar={handleProtoDelete}
              onRenameBar={handleProtoRename}
              onRecodeBar={handleProtoRecode}
              scheduleId={scheduleId}
              paletteCodes={paletteAsExistingCodes}
              selectedIds={protoSelection}
              onSelectionChange={handleSelectionChange}
              onPinLanes={pinLanes}
              previewRefs={trainPreviewRefs}
              trainMappedRefs={trainMappedRefs}
              readOnly={readOnly}
              rowOrder={protoLeafOrder ?? undefined}
              onRowOrderChange={(keys) => {
                pushUndo("Row order");
                setProtoLeafOrder(keys);
              }}
              baselineEndIndex={protoBaselineEndIdx}
              extraRows={protoExtraRows.map((label) => ({ key: label, label }))}
              onRenameRow={(key, label) => {
                pushUndo("Row rename");
                setProtoExtraRows((prev) => prev.map((l) => (l === key ? label : l)));
                setProtoRowOverrides((prev) => {
                  const next = new Map(prev);
                  next.forEach((v, k) => {
                    if (v === key) next.set(k, label);
                  });
                  return next;
                });
                setProtoNewActs((prev) =>
                  prev.map((n) =>
                    n.wbsKey === key ? { ...n, wbsKey: label, wbsPath: [label] } : n,
                  ),
                );
                setProtoLeafOrder((prev) =>
                  prev ? prev.map((k) => (k === key ? label : k)) : prev,
                );
                setProtoExtraLanes((prev) => {
                  const n = prev.get(key);
                  if (n == null) return prev;
                  const next = new Map(prev);
                  next.delete(key);
                  next.set(label, n);
                  return next;
                });
              }}
              onDeleteRow={(key) => {
                pushUndo("Row delete");
                setProtoExtraRows((prev) => prev.filter((l) => l !== key));
                setProtoRowOverrides((prev) => {
                  const next = new Map(prev);
                  next.forEach((v, k) => {
                    if (v === key) next.delete(k);
                  });
                  return next;
                });
                setProtoNewActs((prev) => {
                  const drop = prev.filter((n) => !n.deleted && n.wbsKey === key).map((n) => n.draftId);
                  if (drop.length > 0) {
                    const dropSet = new Set(drop);
                    setPendingOps((ops) => ({
                      ...ops,
                      pending_new_activities: ops.pending_new_activities.filter(
                        (n) => !dropSet.has(n.draft_id),
                      ),
                    }));
                  }
                  return prev.map((n) => (n.wbsKey === key ? { ...n, deleted: true } : n));
                });
              }}
              levelByLeaf={effectiveLevelByLeaf}
              taktContextByLeaf={leafTaktByLeaf}
              barTip={trainBarTip}
              miscodeCountByLeaf={miscodeCountByLeaf}
              notaktLeaves={levelUiActive ? notaktLeaves : undefined}
              taktCMenu={context.takt_menus.Takt_C}
              onSetLeafLevel={!readOnly ? handleSetLeafLevel : undefined}
              onToggleNotakt={levelUiActive && !readOnly ? handleToggleNotakt : undefined}
              extraLanes={protoExtraLanes}
              onChangeLanes={handleChangeLanes}
            />
          </div>
        </div>
        </div>
      </div>
      </div>
        ) : null}
      />
      )}

      {codeEditTarget && (
        <CodeEditModal
          code={codeEditTarget}
          displayCode={paletteByKey.get(codeEditTarget)?.code ?? codeEditTarget}
          codeName={paletteByKey.get(codeEditTarget)?.name ?? codeEditTarget}
          color={paletteByKey.get(codeEditTarget)?.color ?? "#888"}
          activities={codeEditActivities}
          onSave={handleStageRezone}
          onClose={() => setCodeEditTarget(null)}
          onEdit={readOnly ? undefined : (patch) => handleEditWorkCode(codeEditTarget, patch)}
          onDelete={readOnly ? undefined : () => handleDeleteCode(codeEditTarget)}
          onRemoveActivity={readOnly ? undefined : handleRemoveFromCode}
        />
      )}

      {deleteImpact && (
        <DeleteImpactModal
          names={deleteImpact.names}
          ties={deleteImpact.ties}
          inScopeOrphans={deleteImpact.orphans}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteImpact(null)}
        />
      )}

    </div>
  );
};

export default PullPlanGrid;
